"use client"

import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams, Link } from 'react-router-dom';
import { getAuthHeader } from '../utils/auth';
import { isEnglishOperatorMode, withOperatorSurface } from '../utils/operatorMode';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const LEADS_COPY = {
    ja: {
        dateLocale: 'ja-JP',
        unnamed: '名称未設定',
        unnamedShort: '(名称なし)',
        untitledPage: '無題のページ',
        updateSuccess: '情報を更新しました。',
        updateError: '更新エラー',
        updateFailed: '更新失敗',
        errorPrefix: 'エラー: ',
        importFailedPrefix: '失敗: ',
        manualAddFailed: '登録失敗',
        convertConfirm: 'このリードを案件(Deals)に登録しますか？',
        convertSuccessConfirm: '案件化しました！案件管理画面へ移動しますか？',
        scoreConfirm: '全リードのスコアを再計算しますか？',
        visitorConvertSuccess: '案件化しました！案件管理画面で詳細を確認してください。',
        archiveConfirm: 'このリードをアーカイブ（配信対象外）にしますか？\n※一覧から隠れ、メルマガも送られなくなります。',
        main: {
            title: 'Lead Management',
            subtitle: 'インバウンド・広告流入リードの分析と管理',
            calculating: '計算中...',
            recalc: 'スコア再計算',
            add: '新規追加',
            importCsv: 'CSVインポート',
            searchPlaceholder: '会社名、氏名、メールアドレスで検索...',
            noVisitors: '訪問履歴が見つかりませんでした',
            noLeads: 'リードが見つかりませんでした',
            noAiEvents: 'AI流入ログが見つかりませんでした',
            noAnalytics: 'アクセス分析データが見つかりませんでした',
            freeNotice: '⚠️ 無料プランでは最新50件のみ表示されます。全件の閲覧・検索にはアップグレードが必要です。',
        },
        tabs: [
            { id: 'active', label: 'アクティブリード', sub: '広告・Web流入' },
            { id: 'house', label: 'ハウスリスト', sub: 'CSV・手動登録' },
            { id: 'anonymous', label: '匿名ログ', sub: 'お宝ビジター' },
            { id: 'ai', label: 'AI流入', sub: 'AI経由・巡回' },
            { id: 'analytics', label: 'アクセス分析', sub: 'ページ・流入' },
            { id: 'all', label: '全件表示', sub: '統合リスト' }
        ],
        statusOptions: [
            { value: 'new', label: '✨ 未対応 (New)' },
            { value: 'contacted', label: '📞 連絡済み' },
            { value: 'archived', label: '📁 アーカイブ' },
            { value: 'converted', label: '✅ 案件化済み' }
        ],
        filterStatusOptions: [
            { value: 'all', label: 'すべてのステータス' },
            { value: 'new', label: '✨ 未対応 (New)' },
            { value: 'converted', label: '✅ 案件化済み' },
            { value: 'archived', label: '📁 アーカイブ' }
        ],
        statusLabels: {
            new: '未対応',
            contacted: '連絡済み',
            archived: 'アーカイブ',
            converted: '案件化済み'
        },
        upgrade: {
            title: 'この機能は有料プラン限定です',
            body: '全件のリード閲覧、ページ遷移、および検索機能を利用するには、プランのアップグレードが必要です。',
            cta: '🚀 プランをアップグレードする',
            oem: 'アップグレードについては、ご契約先の担当者までお問い合わせください。'
        },
        pagination: { prev: '前へ', next: '次へ' },
        lead: {
            tabs: { info: '基本情報', timeline: '行動履歴', raw: '生データ' },
            trackingStrength: '計測強度',
            registeredAt: '登録日',
            company: '会社名',
            person: '担当者名',
            email: 'メール',
            phone: '電話',
            tags: 'タグ (カンマ区切りで複数可)',
            tagPlaceholder: '例: メルマガ, 2024展示会, Aランク',
            campaignName: 'キャンペーン名',
            adName: '広告クリエイティブ名',
            status: '進捗ステータス',
            convert: 'このリードを案件化する',
            converted: '✅ 案件化済みです',
            goDeals: '案件管理画面へ移動する',
            memo: '内部メモ (スタッフ用)',
            memoPlaceholder: '商談の感触など...',
            saving: '保存中...',
            save: '変更を保存する',
            noTimeline: '履歴が見つかりません',
            noRaw: 'データがありません',
            rawError: '解析エラー',
            rawErrorBody: 'JSONの形式が正しくありません',
            rawContent: '内容',
            createdAt: '登録日時',
            source: '経路',
            leadName: '会社名 / 担当者',
            score: 'スコア',
            clicks: 'クリック',
            actions: '操作',
            detail: '詳細',
            detailTitle: '詳細を確認',
            convertTitle: '案件として登録',
            archive: 'アーカイブ'
        },
        visitor: {
            title: '匿名訪問者の解析',
            registerResult: '調査結果の登録',
            company: '特定した会社名',
            companyPlaceholder: '〇〇株式会社',
            note: '備考・メモ',
            notePlaceholder: 'IPから判明した情報など...',
            inputCompanyAlert: '会社名を入力してください。',
            confirmSuccess: '特定情報を確定しました。',
            genericError: 'エラーが発生しました。',
            noteSuccess: 'メモを保存しました。',
            noteFailed: '保存に失敗しました。',
            saving: '保存中...',
            saveNote: 'メモを保存',
            processing: '処理中...',
            confirmCompany: '会社名を確定',
            convert: '案件化する',
            device: 'デバイス / 通信',
            source: '流入ソース',
            direct: '直接流入 / 検索',
            domesticWhoisTitle: '日本の公式サイト(JPNIC)で調査',
            globalWhoisTitle: '見やすさ重視の調査サイト(IPinfo.io)',
            domesticWhois: 'JPNIC(国内)',
            globalWhois: 'IPinfo(海外)',
            guideButton: '調査のコツ 💡',
            history: '閲覧ページ履歴',
            estimated: '推定',
            identified: '特定済み',
            unidentified: '未特定ビジター',
            lastVisit: '最終訪問',
            visitorCol: '訪問者 (特定状況/UUID)',
            totalPv: '累計PV',
            lastPage: '最後に見たページ',
            ipAddress: 'IPアドレス',
            analyze: '足跡を解析'
        },
        ai: {
            type: '種別',
            source: 'AI元',
            page: 'ページ',
            referrer: '参照元',
            lead: 'リード化',
            detectedAt: '検知日時',
            all: 'すべて',
            aiReferral: 'AI経由アクセス',
            aiCrawler: 'AIクローラー',
            linkedLead: 'リード化済み',
            anonymous: '未リード化',
            visitor: '訪問者',
            latest: '最終検知',
            filterLabel: '表示種別'
        },
        analytics: {
            title: 'アクセス分析',
            rangeLabel: '期間',
            ranges: [
                { value: 'today', label: '今日' },
                { value: '7d', label: '7日' },
                { value: '30d', label: '30日' },
                { value: '90d', label: '90日' },
                { value: 'custom', label: 'カスタム' }
            ],
            startDate: '開始日',
            endDate: '終了日',
            summary: 'サマリー',
            pageSection: 'ページ別',
            sourceSection: '流入元別',
            aiSection: 'AIからのアクセス',
            pageViews: 'アクセス数',
            visitors: '訪問者数',
            conversions: '問い合わせ数',
            cvRate: '問い合わせ率',
            aiVisits: 'AI経由',
            aiCrawlers: 'AIクローラー',
            averageAccess: '平均アクセス',
            trendSection: 'アクセス推移',
            bucketLabel: '集計',
            buckets: [
                { value: 'daily', label: '日別' },
                { value: 'weekly', label: '週別' },
                { value: 'monthly', label: '月別' }
            ],
            source: '流入元',
            page: 'ページ',
            type: '種別',
            latest: '最終検知',
            empty: 'データがありません',
            aiReferral: 'AI流入経由',
            aiCrawler: 'AIクローラー'
        },
        guide: {
            title: '🕵️ 訪問者の「正体」を調べるコツ',
            intro: 'IPアドレスから「会社名」が見つかれば、それは**最高のお宝リード**です。以下のポイントを参考に、結果を読み解いてみましょう！',
            directTitle: '🏢 会社名が直接出た場合',
            directBody: '【本命お宝】 その会社のオフィスからのアクセス確定です。即座に社名を登録して、HPをチェックしましょう。ただしISP会社の場合もあるので、ご注意ください。',
            sharedTitle: '🏢 「Regus」や「WeWork」が出た場合',
            sharedBody: '【シェアオフィス】 特定の1社は絞れませんが、ビジネスマンが仕事で見ていることは確実です。付近の企業へのアプローチのヒントになります。',
            generalTitle: '🏠 「NTT」「Softbank」等が出た場合',
            generalBody: '【一般回線】 スマホや自宅からのアクセスです。無理に特定せず、「どのページを何回見たか（熱量）」に注目しましょう。',
            close: 'わかった！調べてみる'
        },
        manual: {
            title: 'リード手動登録',
            company: '会社名',
            name: '氏名',
            email: 'メールアドレス',
            phone: '電話番号',
            tags: 'タグ',
            tagPlaceholder: '例: メルマガ, 2024展示会, Aランク',
            cancel: 'キャンセル',
            saving: '保存中...',
            save: '保存する'
        },
        import: {
            title: 'CSVインポート',
            selectFile: 'ファイルを選択してください',
            downloadFailed: 'DL失敗',
            fileLabel: 'CSVファイル',
            template: '雛形DL',
            tagsPlaceholder: '一括付与タグ...',
            cancel: 'キャンセル',
            uploading: '処理中...',
            start: 'インポート開始',
            success: 'インポートが完了しました。'
        }
    },
    en: {
        dateLocale: 'en-US',
        unnamed: 'Unnamed lead',
        unnamedShort: '(Unnamed)',
        untitledPage: 'Untitled page',
        updateSuccess: 'Lead details updated.',
        updateError: 'Update error',
        updateFailed: 'Update failed',
        errorPrefix: 'Error: ',
        importFailedPrefix: 'Failed: ',
        manualAddFailed: 'Could not add the lead.',
        convertConfirm: 'Convert this lead into a deal?',
        convertSuccessConfirm: 'Converted to a deal. Open the pipeline now?',
        scoreConfirm: 'Recalculate scores for all leads?',
        visitorConvertSuccess: 'Converted to a deal. You can review it in the pipeline.',
        archiveConfirm: 'Archive this lead and remove it from active outreach lists?',
        main: {
            title: 'Lead Management',
            subtitle: 'Review inbound, web, ad, and manually imported leads.',
            calculating: 'Calculating...',
            recalc: 'Recalculate Scores',
            add: 'Add Lead',
            importCsv: 'Import CSV',
            searchPlaceholder: 'Search by company, name, or email...',
            noVisitors: 'No visitor history found.',
            noLeads: 'No leads found.',
            noAiEvents: 'No AI traffic logs found.',
            noAnalytics: 'No access analytics found.',
            freeNotice: 'Free plans show the latest 50 records only. Upgrade to search and browse all leads.',
        },
        tabs: [
            { id: 'active', label: 'Active Leads', sub: 'Ads & Web' },
            { id: 'house', label: 'House List', sub: 'CSV & Manual' },
            { id: 'anonymous', label: 'Anonymous Visitors', sub: 'Visitor Trails' },
            { id: 'ai', label: 'AI Traffic', sub: 'AI referrals' },
            { id: 'analytics', label: 'Access Analytics', sub: 'Pages & Sources' },
            { id: 'all', label: 'All Leads', sub: 'Unified View' }
        ],
        statusOptions: [
            { value: 'new', label: 'New' },
            { value: 'contacted', label: 'Contacted' },
            { value: 'archived', label: 'Archived' },
            { value: 'converted', label: 'Converted' }
        ],
        filterStatusOptions: [
            { value: 'all', label: 'All statuses' },
            { value: 'new', label: 'New' },
            { value: 'converted', label: 'Converted' },
            { value: 'archived', label: 'Archived' }
        ],
        statusLabels: {
            new: 'New',
            contacted: 'Contacted',
            archived: 'Archived',
            converted: 'Converted'
        },
        upgrade: {
            title: 'Upgrade required',
            body: 'Upgrade your plan to view every lead, move between pages, and use full search.',
            cta: 'Upgrade Plan',
            oem: 'Please contact your account representative to upgrade this workspace.'
        },
        pagination: { prev: 'Previous', next: 'Next' },
        lead: {
            tabs: { info: 'Info', timeline: 'Activity', raw: 'Raw Data' },
            trackingStrength: 'Tracking strength',
            registeredAt: 'Registered',
            company: 'Company',
            person: 'Contact Name',
            email: 'Email',
            phone: 'Phone',
            tags: 'Tags (comma separated)',
            tagPlaceholder: 'Example: newsletter, event 2026, hot lead',
            campaignName: 'Campaign Name',
            adName: 'Ad Creative Name',
            status: 'Lead Status',
            convert: 'Convert to Deal',
            converted: 'Converted to a deal',
            goDeals: 'Open Pipeline',
            memo: 'Internal Memo',
            memoPlaceholder: 'Notes from calls, meetings, or research...',
            saving: 'Saving...',
            save: 'Save Changes',
            noTimeline: 'No activity found.',
            noRaw: 'No data available.',
            rawError: 'Parse error',
            rawErrorBody: 'The JSON format is invalid.',
            rawContent: 'Content',
            createdAt: 'Created',
            source: 'Source',
            leadName: 'Company / Contact',
            score: 'Score',
            clicks: 'Clicks',
            actions: 'Actions',
            detail: 'Details',
            detailTitle: 'View details',
            convertTitle: 'Register as a deal',
            archive: 'Archive'
        },
        visitor: {
            title: 'Anonymous Visitor Analysis',
            registerResult: 'Register Research Result',
            company: 'Identified Company',
            companyPlaceholder: 'Company name',
            note: 'Notes',
            notePlaceholder: 'Add IP research notes or context...',
            inputCompanyAlert: 'Enter a company name first.',
            confirmSuccess: 'Visitor identity confirmed.',
            genericError: 'An error occurred.',
            noteSuccess: 'Memo saved.',
            noteFailed: 'Could not save the memo.',
            saving: 'Saving...',
            saveNote: 'Save Memo',
            processing: 'Processing...',
            confirmCompany: 'Confirm Company',
            convert: 'Convert to Deal',
            device: 'Device / Network',
            source: 'Traffic Source',
            direct: 'Direct / Search',
            domesticWhoisTitle: 'Research with JPNIC',
            globalWhoisTitle: 'Research with IPinfo.io',
            domesticWhois: 'JPNIC',
            globalWhois: 'IPinfo',
            guideButton: 'Research Tips',
            history: 'Page View History',
            estimated: 'Estimated',
            identified: 'Identified',
            unidentified: 'Unidentified visitor',
            lastVisit: 'Last Visit',
            visitorCol: 'Visitor (Identity / UUID)',
            totalPv: 'Total PV',
            lastPage: 'Last Viewed Page',
            ipAddress: 'IP Address',
            analyze: 'Analyze Trail'
        },
        ai: {
            type: 'Type',
            source: 'AI Source',
            page: 'Page',
            referrer: 'Referrer',
            lead: 'Lead',
            detectedAt: 'Detected',
            all: 'All',
            aiReferral: 'AI Referral',
            aiCrawler: 'AI Crawler',
            linkedLead: 'Lead Created',
            anonymous: 'Anonymous',
            visitor: 'Visitor',
            latest: 'Latest',
            filterLabel: 'Event Type'
        },
        analytics: {
            title: 'Access Analytics',
            rangeLabel: 'Period',
            ranges: [
                { value: 'today', label: 'Today' },
                { value: '7d', label: '7 days' },
                { value: '30d', label: '30 days' },
                { value: '90d', label: '90 days' },
                { value: 'custom', label: 'Custom' }
            ],
            startDate: 'Start',
            endDate: 'End',
            summary: 'Summary',
            pageSection: 'By Page',
            sourceSection: 'By Source',
            aiSection: 'AI Access',
            pageViews: 'Accesses',
            visitors: 'Visitors',
            conversions: 'Inquiries',
            cvRate: 'Inquiry Rate',
            aiVisits: 'AI Referrals',
            aiCrawlers: 'AI Crawlers',
            averageAccess: 'Average Access',
            trendSection: 'Access Trend',
            bucketLabel: 'Group by',
            buckets: [
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' }
            ],
            source: 'Source',
            page: 'Page',
            type: 'Type',
            latest: 'Latest',
            empty: 'No data',
            aiReferral: 'AI Referral',
            aiCrawler: 'AI Crawler'
        },
        guide: {
            title: 'Visitor Identity Tips',
            intro: 'When a company name appears from the IP address, treat it as a high-value signal. Use these checks to interpret the result.',
            directTitle: 'Company name appears directly',
            directBody: 'This often means the visitor came from that company office. Register the company and review their website, while remembering that some results can still be an ISP.',
            sharedTitle: 'Shared offices such as Regus or WeWork',
            sharedBody: 'You may not identify one exact company, but the traffic is still likely business-related and can help guide nearby account research.',
            generalTitle: 'Telecom or home/mobile networks',
            generalBody: 'This is usually mobile or residential traffic. Focus on page views and repeat visits instead of forcing identity.',
            close: 'Got it'
        },
        manual: {
            title: 'Add Lead Manually',
            company: 'Company',
            name: 'Name',
            email: 'Email Address',
            phone: 'Phone Number',
            tags: 'Tags',
            tagPlaceholder: 'Example: newsletter, event 2026, hot lead',
            cancel: 'Cancel',
            saving: 'Saving...',
            save: 'Save'
        },
        import: {
            title: 'CSV Import',
            selectFile: 'Select a CSV file first.',
            downloadFailed: 'Template download failed.',
            fileLabel: 'CSV File',
            template: 'Download Template',
            tagsPlaceholder: 'Tags to apply to every imported lead...',
            cancel: 'Cancel',
            uploading: 'Uploading...',
            start: 'Start Import',
            success: 'Import completed.'
        }
    }
};

const getLeadCopy = (isEnglishBeta) => isEnglishBeta ? LEADS_COPY.en : LEADS_COPY.ja;

const formatLeadDateTime = (value, isEnglishBeta = false) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(getLeadCopy(isEnglishBeta).dateLocale);
};

const formatLeadDate = (value, isEnglishBeta = false) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(getLeadCopy(isEnglishBeta).dateLocale);
};

const formatLeadTime = (value, isEnglishBeta = false, options) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString(getLeadCopy(isEnglishBeta).dateLocale, options);
};

const getStatusLabel = (status, isEnglishBeta = false) =>
    getLeadCopy(isEnglishBeta).statusLabels[status] || status || '-';

// --- Icons (UIで使用するすべてのアイコン: 属性エラーを修正済み) ---
const Icons = {
    Search: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
    ArrowRight: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>,
    Archive: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>,
    Upload: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>,
    Zap: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>,
    Chart: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"></line><line x1="18" y1="20" x2="18" y2="4"></line><line x1="6" y1="20" x2="6" y2="16"></line></svg>,
    Refresh: ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>,
    Close: () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
    Plus: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    Download: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
    Eye: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
    Meta: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    Instagram: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>,
    Globe: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>,
    Shield: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>,
};

// --- 追加するアイコンとモーダルコンポーネント ---
const LockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-slate-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>;

function UpgradeModal({ onClose, user, isEnglishBeta = false, accountPath = '/account' }) {
    const copy = getLeadCopy(isEnglishBeta).upgrade;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[150] p-4" onClick={onClose}>
            <div className="bg-white p-5 sm:p-8 rounded-2xl sm:rounded-lg shadow-2xl border w-full max-w-md text-center relative" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><Icons.Close /></button>
                <div className="flex justify-center"><LockIcon /></div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-800 mt-2">{copy.title}</h3>
                <p className="text-slate-600 mt-4 text-sm leading-relaxed">{copy.body}</p>
                
                {/* 🚀 修正: OEMユーザーの場合はリンクを隠す */}
                {!user?.oem_id ? (
                    <Link to={isEnglishBeta ? withOperatorSurface(accountPath) : accountPath} className="mt-8 inline-block bg-indigo-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-indigo-700 transition-colors w-full">
                        {copy.cta}
                    </Link>
                ) : (
                    <div className="mt-8 p-4 bg-blue-50 text-blue-700 font-bold rounded-xl border border-blue-100">
                        {copy.oem}
                    </div>
                )}
            </div>
        </div>
    );
}

const getSourceIcon = (label) => {
        if (label?.includes('AI流入') || label?.includes('AI検索') || label?.includes('AI Traffic') || label?.includes('AI Search')) return <Icons.Zap className="text-violet-600" />;
        if (label?.includes('Instagram')) return <Icons.Instagram className="text-pink-500" />;
        if (label?.includes('Meta')) return <Icons.Meta className="text-blue-600" />;
        return <Icons.Globe className="text-gray-400" />;
    };

const getHostname = (value) => {
    if (!value) return '';
    try {
        return new URL(value).hostname.replace(/^www\./, '');
    } catch {
        return '';
    }
};

const getVisitorSourceLabel = (visitor = {}, isEnglishBeta = false) => {
    const visitorCopy = getLeadCopy(isEnglishBeta).visitor;
    const sourceChannel = (visitor.source_channel || '').toLowerCase();
    const sourceName = visitor.source_name || '';
    const utmSource = visitor.utm_source || '';
    const utmMedium = (visitor.utm_medium || '').toLowerCase();
    const src = utmSource.toLowerCase();

    if (sourceChannel === 'ai_search') {
        return isEnglishBeta ? `AI Traffic: ${sourceName || 'AI'}` : `AI流入: ${sourceName || 'AI'}`;
    }
    if (sourceName && sourceChannel) return sourceName;
    if (src === 'ig' || src === 'instagram') return 'Instagram';
    if (src === 'an') return isEnglishBeta ? 'Meta Audience Network' : 'Meta広告';
    if (src === 'msg' || src === 'messenger') return 'Messenger';
    if (src === 'th' || src === 'threads') return 'Threads';
    if (src === 'fb' || src === 'facebook' || src === 'meta') return 'Meta広告';
    if (src === 'google') return utmMedium.includes('cpc') ? 'Google広告' : 'Google';
    if (utmSource) return utmSource;

    const referrerHost = getHostname(visitor.referrer);
    const ownHost = getHostname(visitor.landing_url || visitor.last_view_url);
    if (referrerHost && (!ownHost || referrerHost !== ownHost)) return referrerHost;
    return visitorCopy.direct;
};

const getVisitorSourceMeta = (visitor = {}) => {
    const parts = [];
    if (visitor.utm_campaign) parts.push(visitor.utm_campaign);
    if (visitor.utm_medium) parts.push(String(visitor.utm_medium).toUpperCase());
    if (parts.length > 0) return parts.join(' / ');
    if (visitor.source_domain) return visitor.source_domain;

    const referrerHost = getHostname(visitor.referrer);
    if (referrerHost) return referrerHost;
    return 'ORGANIC';
};

const getVisitorSourceDetail = (visitor = {}) => visitor.referrer || visitor.landing_url || '';

const formatAnalyticsNumber = (value, isEnglishBeta = false) =>
    Number(value || 0).toLocaleString(getLeadCopy(isEnglishBeta).dateLocale);

const getAnalyticsSourceLabel = (label, isEnglishBeta = false) => {
    if (!isEnglishBeta) return label || '-';
    const map = {
        '直接流入': 'Direct',
        '外部サイト': 'External Site',
        'Meta広告': 'Meta Ads',
        'Google広告': 'Google Ads',
        'AI検索': 'AI Traffic',
        'AI流入': 'AI Traffic',
    };
    return map[label] || label || '-';
};

const getDateInputValue = (daysAgo = 0) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
};

function AccessAnalyticsPanel({ analytics, isLoading, isEnglishBeta = false }) {
    const copy = getLeadCopy(isEnglishBeta);
    const analyticsCopy = copy.analytics;
    const [expandedPageUrl, setExpandedPageUrl] = useState(null);
    const [chartTooltip, setChartTooltip] = useState(null);
    const summary = analytics?.summary || {};
    const timeline = analytics?.timeline || [];
    const pages = analytics?.pages || [];
    const sources = analytics?.sources || [];
    const aiRows = analytics?.ai || [];
    const aiTimeline = analytics?.ai_timeline || [];
    const maxTrendViews = Math.max(1, ...timeline.map(row => Number(row.page_views || 0)));
    const maxSourceViews = Math.max(1, ...sources.map(row => Number(row.page_views || 0)));

    const formatBucketLabel = (value) => {
        if (!value) return '-';
        const date = new Date(`${value}T00:00:00`);
        if (Number.isNaN(date.getTime())) return value;
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return analytics?.period?.bucket === 'monthly' ? `${date.getFullYear()}/${month}` : `${month}/${day}`;
    };

    const buildTrendTooltip = (row) => {
        const aiTotal = Number(row.ai_visits || 0) + Number(row.ai_crawlers || 0);
        return [
            `${formatBucketLabel(row.bucket_label)}`,
            `${analyticsCopy.pageViews}: ${formatAnalyticsNumber(row.page_views || row.count, isEnglishBeta)}`,
            row.visitors !== undefined ? `${analyticsCopy.visitors}: ${formatAnalyticsNumber(row.visitors, isEnglishBeta)}` : null,
            row.conversions !== undefined ? `${analyticsCopy.conversions}: ${formatAnalyticsNumber(row.conversions, isEnglishBeta)}` : null,
            aiTotal > 0 ? `${analyticsCopy.aiVisits}: ${formatAnalyticsNumber(aiTotal, isEnglishBeta)}` : null,
        ].filter(Boolean).join('\n');
    };

    const updateChartTooltip = (event, content) => {
        const width = 220;
        const height = 120;
        const rect = event.currentTarget?.getBoundingClientRect?.();
        const rawLeft = typeof event.clientX === 'number' ? event.clientX : (rect ? rect.left + rect.width / 2 : 12);
        const rawTop = typeof event.clientY === 'number' ? event.clientY : (rect ? rect.top : 12);
        const left = Math.min(rawLeft + 14, Math.max(12, window.innerWidth - width - 12));
        const top = Math.min(rawTop + 14, Math.max(12, window.innerHeight - height - 12));
        setChartTooltip({ content, left, top });
    };

    const MetricCard = ({ label, value, tone = 'blue' }) => {
        const tones = {
            blue: 'bg-blue-50 border-blue-100 text-blue-700',
            green: 'bg-green-50 border-green-100 text-green-700',
            violet: 'bg-violet-50 border-violet-100 text-violet-700',
            slate: 'bg-slate-50 border-slate-100 text-slate-700',
            amber: 'bg-amber-50 border-amber-100 text-amber-700',
        };
        return (
            <div className={`rounded-2xl border p-4 ${tones[tone] || tones.blue}`}>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-60">{label}</p>
                <p className="mt-2 text-2xl font-black">{value}</p>
            </div>
        );
    };

    const TrendBars = ({ rows, maxValue, heightClass = 'h-40', showAi = true }) => (
        <div className="max-w-full overflow-x-auto overscroll-x-contain pb-1">
            <div className="min-w-full">
                <div
                    className={`grid min-w-full items-end gap-1 border-b border-gray-100 px-1 ${heightClass}`}
                    style={{ gridTemplateColumns: `repeat(${Math.max(rows.length, 1)}, minmax(24px, 1fr))` }}
                >
                {rows.map((row, index) => {
                    const views = Number(row.page_views || row.count || 0);
                    const aiTotal = Number(row.ai_visits || 0) + Number(row.ai_crawlers || 0);
                    const height = Math.max(3, Math.round((views / Math.max(1, maxValue)) * 100));
                    const aiHeight = showAi && views > 0 ? Math.min(100, Math.round((aiTotal / views) * 100)) : 0;
                    const tooltip = buildTrendTooltip(row);
                    return (
                        <div
                            key={`${row.bucket_label}-${index}`}
                            className="group relative flex h-full min-w-0 items-end justify-center"
                            title={tooltip}
                            aria-label={tooltip}
                            onMouseEnter={(event) => updateChartTooltip(event, tooltip)}
                            onMouseMove={(event) => updateChartTooltip(event, tooltip)}
                            onMouseLeave={() => setChartTooltip(null)}
                            onFocus={(event) => updateChartTooltip(event, tooltip)}
                            onBlur={() => setChartTooltip(null)}
                        >
                            <div className="relative w-full max-w-8 overflow-hidden rounded-t-md bg-blue-500 transition-opacity group-hover:opacity-80" style={{ height: `${height}%`, minHeight: views > 0 ? '4px' : 0 }}>
                                {aiHeight > 0 && <div className="absolute bottom-0 left-0 right-0 bg-violet-500/80" style={{ height: `${aiHeight}%` }} />}
                            </div>
                        </div>
                    );
                })}
                </div>
                <div
                    className="mt-2 grid min-w-full gap-1 px-1"
                    style={{ gridTemplateColumns: `repeat(${Math.max(rows.length, 1)}, minmax(24px, 1fr))` }}
                >
                    {rows.map((row, index) => (
                        <span key={`${row.bucket_label}-${index}-label`} className="truncate text-center text-[9px] font-bold text-gray-400">
                            {formatBucketLabel(row.bucket_label)}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );

    const PageMiniTrend = ({ rows = [] }) => {
        const maxValue = Math.max(1, ...rows.map(row => Number(row.page_views || 0)));
        if (rows.length === 0) return null;
        return (
            <div className="mt-3 max-w-full overflow-hidden rounded-xl bg-gray-50 p-3">
                <TrendBars rows={rows} maxValue={maxValue} heightClass="h-24" />
            </div>
        );
    };

    const aiBuckets = Array.from(new Set(aiTimeline.map(row => row.bucket_label))).sort();
    const aiSourceNames = Array.from(new Set(aiTimeline.map(row => row.source_name || 'AI'))).slice(0, 5);
    const aiColorClasses = ['bg-violet-600', 'bg-indigo-500', 'bg-fuchsia-500', 'bg-slate-500', 'bg-blue-500'];
    const aiBucketTotals = aiBuckets.map(bucketLabel => ({
        bucket_label: bucketLabel,
        total: aiTimeline.filter(row => row.bucket_label === bucketLabel).reduce((sum, row) => sum + Number(row.count || 0), 0),
    }));
    const maxAiTotal = Math.max(1, ...aiBucketTotals.map(row => row.total));

    const AiStackedBars = () => (
        <div className="max-w-full overflow-x-auto overscroll-x-contain pb-1">
            <div className="min-w-full">
                <div
                    className="grid min-w-full items-end gap-1 h-36 border-b border-violet-100 px-1"
                    style={{ gridTemplateColumns: `repeat(${Math.max(aiBuckets.length, 1)}, minmax(24px, 1fr))` }}
                >
                {aiBuckets.map(bucketLabel => {
                    const bucketRows = aiTimeline.filter(row => row.bucket_label === bucketLabel);
                    const total = bucketRows.reduce((sum, row) => sum + Number(row.count || 0), 0);
                    const height = Math.max(4, Math.round((total / maxAiTotal) * 100));
                    const tooltip = [
                        `${formatBucketLabel(bucketLabel)}`,
                        ...aiSourceNames.map(sourceName => {
                            const count = bucketRows
                                .filter(row => (row.source_name || 'AI') === sourceName)
                                .reduce((sum, row) => sum + Number(row.count || 0), 0);
                            return count > 0 ? `${sourceName}: ${formatAnalyticsNumber(count, isEnglishBeta)}` : null;
                        }).filter(Boolean),
                        `${analyticsCopy.pageViews}: ${formatAnalyticsNumber(total, isEnglishBeta)}`,
                    ].join('\n');
                    return (
                        <div
                            key={bucketLabel}
                            className="group relative flex h-full min-w-0 items-end justify-center"
                            title={tooltip}
                            aria-label={tooltip}
                            onMouseEnter={(event) => updateChartTooltip(event, tooltip)}
                            onMouseMove={(event) => updateChartTooltip(event, tooltip)}
                            onMouseLeave={() => setChartTooltip(null)}
                            onFocus={(event) => updateChartTooltip(event, tooltip)}
                            onBlur={() => setChartTooltip(null)}
                        >
                            <div className="flex w-full max-w-8 flex-col-reverse overflow-hidden rounded-t-md bg-gray-100 transition-opacity group-hover:opacity-80" style={{ height: `${height}%`, minHeight: total > 0 ? '4px' : 0 }}>
                                {aiSourceNames.map((sourceName, idx) => {
                                    const count = bucketRows
                                        .filter(row => (row.source_name || 'AI') === sourceName)
                                        .reduce((sum, row) => sum + Number(row.count || 0), 0);
                                    if (!count || total <= 0) return null;
                                    return (
                                        <div
                                            key={sourceName}
                                            className={aiColorClasses[idx] || 'bg-gray-400'}
                                            style={{ height: `${Math.max(8, Math.round((count / total) * 100))}%` }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
                </div>
                <div
                    className="mt-2 grid min-w-full gap-1 px-1"
                    style={{ gridTemplateColumns: `repeat(${Math.max(aiBuckets.length, 1)}, minmax(24px, 1fr))` }}
                >
                    {aiBuckets.map(bucketLabel => (
                        <span key={`${bucketLabel}-label`} className="truncate text-center text-[9px] font-bold text-gray-400">
                            {formatBucketLabel(bucketLabel)}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );

    if (isLoading) {
        return (
            <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center shadow-sm">
                <Icons.Refresh className="animate-spin inline-block text-blue-500 w-8 h-8" />
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center font-bold text-gray-400 shadow-sm">
                {copy.main.noAnalytics}
            </div>
        );
    }

    return (
        <div className="min-w-0 space-y-6 overflow-hidden">
            {chartTooltip && (
                <div
                    className="pointer-events-none fixed z-[9999] max-w-[220px] whitespace-pre rounded-lg bg-gray-900 px-3 py-2 text-[11px] font-bold leading-relaxed text-white shadow-xl"
                    style={{ left: chartTooltip.left, top: chartTooltip.top }}
                >
                    {chartTooltip.content}
                </div>
            )}
            <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                <div className="mb-4 flex items-center gap-2">
                    <Icons.Chart />
                    <h2 className="text-sm font-black text-gray-900">{analyticsCopy.summary}</h2>
                </div>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                    <MetricCard label={analyticsCopy.pageViews} value={formatAnalyticsNumber(summary.page_views, isEnglishBeta)} />
                    <MetricCard label={analyticsCopy.visitors} value={formatAnalyticsNumber(summary.visitors, isEnglishBeta)} tone="slate" />
                    <MetricCard label={analyticsCopy.conversions} value={formatAnalyticsNumber(summary.conversions, isEnglishBeta)} tone="green" />
                    <MetricCard label={analyticsCopy.aiVisits} value={formatAnalyticsNumber(Number(summary.ai_visits || 0) + Number(summary.ai_crawlers || 0), isEnglishBeta)} tone="violet" />
                    <MetricCard label={analyticsCopy.averageAccess} value={formatAnalyticsNumber(summary.average_page_views, isEnglishBeta)} tone="amber" />
                </div>
            </section>

            <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h2 className="text-sm font-black text-gray-900">{analyticsCopy.trendSection}</h2>
                        <p className="text-[10px] font-bold text-gray-400">{analyticsCopy.averageAccess}: {formatAnalyticsNumber(summary.average_page_views, isEnglishBeta)}</p>
                    </div>
                    <div className="flex flex-wrap gap-3 text-[10px] font-bold text-gray-500">
                        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" />{analyticsCopy.pageViews}</span>
                        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-violet-500" />{analyticsCopy.aiVisits}</span>
                    </div>
                </div>
                {timeline.length === 0 ? (
                    <div className="py-8 text-center text-sm font-bold text-gray-400">{analyticsCopy.empty}</div>
                ) : (
                    <TrendBars rows={timeline} maxValue={maxTrendViews} />
                )}
            </section>

            <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
                <section className="min-w-0 overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                    <h2 className="mb-4 text-sm font-black text-gray-900">{analyticsCopy.pageSection}</h2>
                    <div className="divide-y divide-gray-100">
                        {pages.length === 0 ? (
                            <div className="py-8 text-center text-sm font-bold text-gray-400">{analyticsCopy.empty}</div>
                        ) : pages.map(row => {
                            const aiTotal = Number(row.ai_visits || 0) + Number(row.ai_crawlers || 0);
                            const expanded = expandedPageUrl === row.url;
                            return (
                                <div key={row.url} className="py-3">
                                    <button
                                        type="button"
                                        onClick={() => setExpandedPageUrl(expanded ? null : row.url)}
                                        className="flex w-full min-w-0 flex-col gap-2 text-left sm:flex-row sm:items-center sm:justify-between"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-black text-gray-900" title={row.page_title || copy.untitledPage}>{row.page_title || copy.untitledPage}</p>
                                            <p className="mt-1 truncate text-[10px] font-mono text-gray-400" title={row.url}>{row.url}</p>
                                        </div>
                                        <div className="flex shrink-0 items-center gap-3">
                                            {aiTotal > 0 && <span className="rounded-full bg-violet-50 px-2 py-1 text-[9px] font-black text-violet-700">AI {formatAnalyticsNumber(aiTotal, isEnglishBeta)}</span>}
                                            <span className="text-lg font-black text-blue-700">{formatAnalyticsNumber(row.page_views, isEnglishBeta)}</span>
                                        </div>
                                    </button>
                                    {expanded && <PageMiniTrend rows={row.timeline || []} />}
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section className="min-w-0 overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                    <h2 className="mb-4 text-sm font-black text-gray-900">{analyticsCopy.sourceSection}</h2>
                    <div className="space-y-4">
                        {sources.length === 0 ? (
                            <div className="py-8 text-center text-sm font-bold text-gray-400">{analyticsCopy.empty}</div>
                        ) : sources.map(row => {
                            const width = Math.max(4, Math.round((Number(row.page_views || 0) / maxSourceViews) * 100));
                            return (
                                <div key={row.source_label}>
                                    <div className="mb-1 flex items-center justify-between gap-3">
                                        <div className="flex min-w-0 items-center gap-2">
                                            {getSourceIcon(getAnalyticsSourceLabel(row.source_label, isEnglishBeta))}
                                            <span className="truncate text-sm font-black text-gray-800">{getAnalyticsSourceLabel(row.source_label, isEnglishBeta)}</span>
                                        </div>
                                        <span className="shrink-0 text-sm font-black text-gray-700">{formatAnalyticsNumber(row.page_views, isEnglishBeta)}</span>
                                    </div>
                                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                                        <div className="h-full rounded-full bg-blue-500" style={{ width: `${width}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>

            <section className="rounded-2xl border border-violet-100 bg-white p-4 shadow-sm sm:p-6">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <h2 className="text-sm font-black text-gray-900">{analyticsCopy.aiSection}</h2>
                    <div className="flex flex-wrap gap-2">
                        {aiSourceNames.map((sourceName, idx) => (
                            <span key={sourceName} className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1 text-[9px] font-bold text-gray-500">
                                <span className={`h-2 w-2 rounded-full ${aiColorClasses[idx] || 'bg-gray-400'}`} />{sourceName}
                            </span>
                        ))}
                    </div>
                </div>
                {aiTimeline.length === 0 ? (
                    <div className="py-8 text-center text-sm font-bold text-gray-400">{analyticsCopy.empty}</div>
                ) : (
                    <AiStackedBars />
                )}
                <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {aiRows.map(row => (
                        <div key={`${row.event_type}-${row.source_name}-${row.source_domain}`} className="rounded-xl border border-violet-50 bg-violet-50/40 p-4">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-black uppercase ${row.event_type === 'ai_crawler' ? 'bg-slate-100 text-slate-600' : 'bg-violet-100 text-violet-700'}`}>
                                {row.event_type === 'ai_crawler' ? analyticsCopy.aiCrawler : analyticsCopy.aiReferral}
                            </span>
                            <div className="mt-2 flex items-end justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-black text-gray-900">{row.source_name || 'AI'}</p>
                                    <p className="truncate text-[10px] font-mono text-gray-400">{row.source_domain || '-'}</p>
                                </div>
                                <p className="text-xl font-black text-violet-700">{formatAnalyticsNumber(row.count, isEnglishBeta)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

// --- ページネーション ---
function Pagination({ currentPage, totalPages, onPageChange, isEnglishBeta = false }) {
    const copy = getLeadCopy(isEnglishBeta).pagination;
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-center space-x-2 mt-4">
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm font-medium">{copy.prev}</button>
            <span className="text-sm font-bold text-gray-600 px-4">{currentPage} / {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm font-medium">{copy.next}</button>
        </div>
    );
}

// --- リード詳細サイドバー ---
function LeadDetailSidebar({ lead, onClose, clientId, onUpdate, onConvert, isEnglishBeta = false, dealsPath = '/app/deals' }) {
    const copy = getLeadCopy(isEnglishBeta);
    const leadCopy = copy.lead;
    const [timeline, setTimeline] = useState([]);
    const [isLoadingTimeline, setIsLoadingTimeline] = useState(true);
    const [activeTab, setActiveTab] = useState('info'); 
    
    const [editForm, setEditForm] = useState({
        company_name: lead.company_name || '',
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        status: lead.status || 'new',
        internal_memo: lead.internal_memo || '',
        tags: lead.tags || ''
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchTimeline = async () => {
            setIsLoadingTimeline(true);
            try {
                const res = await fetch(`${API_URL}/api/marketing/leads/${lead.id}/timeline?client_id=${clientId}`, {
                    headers: getAuthHeader()
                });
                const data = await res.json();
                if (data.success) setTimeline(data.timeline);
            } catch (error) {
                console.error("Timeline Fetch Error:", error);
            } finally {
                setIsLoadingTimeline(false);
            }
        };
        fetchTimeline();
    }, [lead.id, clientId]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onUpdate(lead.id, editForm);
            alert(copy.updateSuccess);
        } finally {
            setIsSaving(false);
        }
    };

    const handleConvertInSidebar = async () => {
        if (await onConvert(lead.id)) {
            onClose(); 
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
                
                <div className="p-4 sm:p-6 border-b bg-gray-50/50">
                    <div className="flex justify-between items-start mb-4">
                        <div className="min-w-0 flex-grow">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="min-w-0 break-words font-bold text-xl sm:text-2xl text-gray-900 leading-tight">
                                    {editForm.company_name || editForm.name || copy.unnamed}
                                </h3>
                                <div className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                    lead.tracking_strength === 'high' ? 'bg-green-100 text-green-700' :
                                    lead.tracking_strength === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                    {leadCopy.trackingStrength}: {lead.tracking_strength}
                                </div>
                            </div>
                            <p className="text-sm text-gray-500 font-medium">{leadCopy.registeredAt}: {formatLeadDateTime(lead.created_at, isEnglishBeta)}</p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full text-gray-400 transition-colors"><Icons.Close /></button>
                    </div>

                    <div className="flex gap-1 bg-gray-200/50 p-1 rounded-xl">
                        {[
                            { id: 'info', label: leadCopy.tabs.info },
                            { id: 'timeline', label: leadCopy.tabs.timeline },
                            { id: 'raw', label: leadCopy.tabs.raw }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                                    activeTab === tab.id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-grow overflow-y-auto p-4 sm:p-6">
                    {activeTab === 'info' && (
                        <div className="space-y-6">
                            {/* --- ★ 新設：詳細属性バッジエリア --- */}
                            <div className="flex flex-wrap gap-2 mb-2">
                                {/* 計測強度バッジ */}
                                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase border shadow-sm ${
                                    lead.tracking_strength === 'high' ? 'bg-green-500 text-white border-green-600' :
                                    lead.tracking_strength === 'medium' ? 'bg-yellow-400 text-white border-yellow-500' : 
                                    'bg-gray-100 text-gray-400 border-gray-200'
                                }`}>
                                    <Icons.Shield /> {lead.tracking_strength === 'high' ? 'CAPI OK' : `Strength: ${lead.tracking_strength}`}
                                </div>

                                {/* 環境（デバイス/OS）バッジ */}
                                <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-[10px] font-bold text-gray-600 border border-gray-200 shadow-sm">
                                    <span>{lead.user_agent?.includes('iPhone') || lead.user_agent?.includes('Android') ? '📱' : '💻'}</span>
                                    <span className="uppercase tracking-tighter">
                                        {lead.user_agent?.includes('Mac') ? 'macOS' : 
                                        lead.user_agent?.includes('Win') ? 'Windows' : 
                                        lead.user_agent?.includes('iPhone') ? 'iOS' : 
                                        lead.user_agent?.includes('Android') ? 'Android' : 'Other OS'}
                                    </span>
                                </div>

                                {/* 流入詳細（Form IDなど） */}
                                {lead.source_detail && (
                                    <div className="flex items-center gap-1.5 px-3 py-1 bg-slate-800 text-slate-300 rounded-full text-[9px] font-mono border border-slate-700">
                                        {lead.source_detail}
                                    </div>
                                )}
                            </div>

                            {/* --- 基本入力項目 --- */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{leadCopy.company}</label>
                                    <input type="text" className="w-full p-2.5 bg-gray-50 border rounded-lg text-sm font-bold" value={editForm.company_name} onChange={e => setEditForm({...editForm, company_name: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{leadCopy.person}</label>
                                    <input type="text" className="w-full p-2.5 bg-gray-50 border rounded-lg text-sm font-bold" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{leadCopy.email}</label>
                                    <input type="email" className="w-full p-2.5 bg-gray-50 border rounded-lg text-sm" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{leadCopy.phone}</label>
                                    <input type="tel" className="w-full p-2.5 bg-gray-50 border rounded-lg text-sm" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
                                </div>
                                <div className="sm:col-span-2 p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">{leadCopy.tags}</label>
                                    <input 
                                        type="text" 
                                        className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
                                        placeholder={leadCopy.tagPlaceholder} 
                                        value={editForm.tags} 
                                        onChange={e => setEditForm({...editForm, tags: e.target.value})} 
                                    />
                                </div>

                                {/* Meta広告情報セクション */}
                                <div className="sm:col-span-2 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50 space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Icons.Meta className="w-4 h-4" />
                                        <span className="text-[10px] font-black text-blue-800 uppercase tracking-wider">Ad Campaign Info</span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2">
                                        <div>
                                            <label className="block text-[9px] font-bold text-blue-400 uppercase">{leadCopy.campaignName}</label>
                                            <p className="text-sm font-bold text-gray-800">{lead.fb_campaign_name || lead.utm_campaign || '---'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-[9px] font-bold text-blue-400 uppercase">{leadCopy.adName}</label>
                                            <p className="text-sm font-bold text-gray-800">{lead.fb_ad_name || lead.utm_content || '---'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-[9px] font-bold text-blue-400 uppercase">Meta Lead ID</label>
                                            <p className="text-[10px] font-mono text-gray-500">{lead.meta_lead_id || '---'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* --- ステータスとアクション --- */}
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{leadCopy.status}</label>
                                <select 
                                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-bold" 
                                    value={editForm.status} 
                                    onChange={e => setEditForm({...editForm, status: e.target.value})}
                                    disabled={lead.status === 'converted'}
                                >
                                    {copy.statusOptions
                                        .filter(option => option.value !== 'converted' || lead.status === 'converted')
                                        .map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                </select>
                            </div>

                            {lead.status !== 'converted' ? (
                                <div className="p-4 bg-green-50 rounded-2xl border border-green-100 flex flex-col gap-3">
                                    <div className="flex items-center gap-2">
                                        <div className="p-1.5 bg-green-600 text-white rounded-lg"><Icons.ArrowRight /></div>
                                        <p className="text-xs font-black text-green-800 uppercase">Deal Conversion</p>
                                    </div>
                                    <button 
                                        onClick={handleConvertInSidebar}
                                        className="w-full py-2.5 bg-green-600 text-white rounded-xl font-black text-xs hover:bg-green-700 shadow-lg shadow-green-100 transition-all"
                                    >
                                        {leadCopy.convert}
                                    </button>
                                </div>
                            ) : (
                                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 text-center">
                                    <p className="text-xs font-bold text-blue-700">{leadCopy.converted}</p>
                                    <button 
                                        onClick={() => window.location.href=`${dealsPath}?searchTerm=${encodeURIComponent(lead.company_name || lead.name)}`}
                                        className="mt-2 text-[10px] text-blue-600 underline font-bold"
                                    >
                                        {leadCopy.goDeals}
                                    </button>
                                </div>
                            )}

                            {/* --- 内部メモと保存ボタン --- */}
                            <div className="pt-4 border-t">
                                <label className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase mb-2">
                                    <Icons.Chart className="w-3 h-3"/> {leadCopy.memo}
                                </label>
                                <textarea className="w-full p-4 bg-yellow-50/30 border border-yellow-100 rounded-xl text-sm min-h-[120px] placeholder:text-gray-300" value={editForm.internal_memo} onChange={e => setEditForm({...editForm, internal_memo: e.target.value})} placeholder={leadCopy.memoPlaceholder} />
                            </div>
                            
                            <button 
                                onClick={handleSave} 
                                disabled={isSaving} 
                                className="w-full py-3 bg-blue-600 text-white rounded-xl font-black text-sm hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all disabled:opacity-50"
                            >
                                {isSaving ? leadCopy.saving : leadCopy.save}
                            </button>
                        </div>
                    )}

                    {/* 行動履歴タブ */}
                    {activeTab === 'timeline' && (
                        <div className="relative pl-4">
                            <div className="absolute top-0 bottom-0 left-[27px] w-0.5 bg-gray-100 -z-10" />
                            {isLoadingTimeline ? (
                                <div className="flex justify-center py-10"><Icons.Refresh className="animate-spin text-gray-300"/></div>
                            ) : timeline.length === 0 ? (
                                <p className="text-center text-gray-400 text-sm py-10 font-bold">{leadCopy.noTimeline}</p>
                            ) : (
                                <div className="space-y-6">
                                    {timeline.map((item, idx) => (
                                        <div key={idx} className="flex gap-4 items-start bg-white border border-gray-100 p-4 rounded-xl shadow-sm">
                                            <div className="p-2 bg-gray-50 rounded-lg text-gray-500">{item.type === 'web' ? <Icons.Globe /> : <Icons.Zap />}</div>
                                            <div className="flex-grow min-w-0">
                                                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center mb-1">
                                                    <span className="text-[10px] font-black uppercase text-blue-500">{item.type}</span>
                                                    <span className="text-[10px] text-gray-400 font-mono">{formatLeadDateTime(item.timestamp, isEnglishBeta)}</span>
                                                </div>
                                                <p className="text-sm font-bold text-gray-800 truncate">{item.summary}</p>
                                                {item.detail && <p className="text-xs text-gray-500 mt-1 break-all line-clamp-2">{item.detail}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 生データタブ */}
                    {activeTab === 'raw' && (
                        <div className="bg-gray-900 rounded-xl p-5 overflow-x-auto">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <span className="text-[10px] font-mono text-green-500 font-bold uppercase tracking-widest">Raw Submission Data</span>
                            </div>
                            {lead.raw_data ? (
                                <div className="space-y-3">
                                    {Object.entries(lead.raw_data).map(([key, val]) => (
                                        <div key={key} className="flex border-b border-gray-800 pb-2">
                                            <span className="w-1/3 text-[10px] font-mono text-gray-500 uppercase">{key}</span>
                                            <span className="w-2/3 text-xs text-gray-300 font-medium break-all">{String(val) || '-'}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-gray-600 text-xs italic text-center py-4">{leadCopy.noRaw}</p>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- 新設：IP調査ガイドモーダル ---
function IdentityGuideModal({ onClose, isEnglishBeta = false }) {
    const copy = getLeadCopy(isEnglishBeta).guide;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
            <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="p-4 sm:p-6 border-b bg-gray-50 flex justify-between items-start gap-4">
                    <h3 className="font-black text-lg sm:text-xl text-gray-800">{copy.title}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><Icons.Close /></button>
                </div>
                <div className="p-5 sm:p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                    <p className="text-sm text-gray-600 leading-relaxed font-medium">
                        {copy.intro}
                    </p>
                    
                    <div className="space-y-4">
                        <div className="p-4 bg-green-50 rounded-2xl border border-green-100">
                            <div className="font-black text-green-700 text-sm mb-1">{copy.directTitle}</div>
                            <p className="text-xs text-green-600 leading-relaxed">
                                {copy.directBody}
                            </p>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
                            <div className="font-black text-blue-700 text-sm mb-1">{copy.sharedTitle}</div>
                            <p className="text-xs text-blue-600 leading-relaxed">
                                {copy.sharedBody}
                            </p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <div className="font-black text-gray-500 text-sm mb-1">{copy.generalTitle}</div>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                {copy.generalBody}
                            </p>
                        </div>
                    </div>

                    <div className="pt-4 border-t text-center">
                        <button onClick={onClose} className="px-8 py-3 bg-gray-900 text-white font-black rounded-xl text-sm hover:bg-gray-800 shadow-xl transition-all">
                            {copy.close}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// 匿名ビジター専用の詳細サイドバー
function VisitorDetailSidebar({ visitor, onClose, clientId, onConfirmSuccess, onConvert, isEnglishBeta = false }) {
    const copy = getLeadCopy(isEnglishBeta);
    const visitorCopy = copy.visitor;
    const [timeline, setTimeline] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showGuide, setShowGuide] = useState(false);
    const [companyName, setCompanyName] = useState(visitor.company_name || "");
    const [notes, setNotes] = useState(visitor.client_memo || ""); // 初期値に既存メモをセット
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const visitorSourceLabel = getVisitorSourceLabel(visitor, isEnglishBeta);
    const visitorSourceMeta = getVisitorSourceMeta(visitor);
    const visitorSourceDetail = getVisitorSourceDetail(visitor);

    useEffect(() => {
        const fetchTimeline = async () => {
            try {
                const res = await fetch(`${API_URL}/api/marketing/visitors/${visitor.visitor_id}/timeline?client_id=${clientId}`, {
                    headers: getAuthHeader()
                });
                const data = await res.json();
                if (data.success) setTimeline(data.timeline);
            } finally { setIsLoading(false); }
        };
        fetchTimeline();
    }, [visitor.visitor_id, clientId]);

    const handleConfirmName = async () => {
        if (!companyName) return alert(visitorCopy.inputCompanyAlert);
        setIsConfirming(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/visitors/confirm`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ 
                    visitor_id: visitor.visitor_id, 
                    company_name: companyName 
                })
            });
            const result = await res.json();
            if (result.success) {
                alert(visitorCopy.confirmSuccess);
                // ★ ここで親から渡された更新関数を呼ぶ！
                if (onConfirmSuccess) onConfirmSuccess(); 
                onClose(); // 確定したらサイドバーを閉じるとスムーズです
            }
        } catch (e) { 
            alert(visitorCopy.genericError); 
        } finally {
            setIsConfirming(false);
        }
    };

    const handleSaveNoteOnly = async () => {
        setIsSavingNote(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/visitors/note`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({
                    visitor_id: visitor.visitor_id,
                    client_id: clientId,
                    custom_company_name: companyName,
                    memo: notes
                })
            });
            if ((await res.json()).success) {
                alert(visitorCopy.noteSuccess);
            }
        } catch (e) {
            alert(visitorCopy.noteFailed);
        } finally {
            setIsSavingNote(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col">
                <div className="p-4 sm:p-6 border-b bg-gray-50 flex justify-between items-start gap-4">
                    <div className="min-w-0">
                        <h3 className="font-bold text-lg sm:text-xl text-gray-900">{visitorCopy.title}</h3>
                        <p className="break-all text-xs text-gray-500 font-mono">ID: {visitor.visitor_id}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full"><Icons.Close /></button>
                </div>

                <div className="flex-grow overflow-y-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
                    {/* 1. クライアントが自分で情報を入れるセクション */}
                    <div className="bg-blue-50 p-4 sm:p-6 rounded-2xl border border-blue-100 space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-blue-600 text-white rounded-lg"><Icons.Search /></div>
                            <span className="text-xs font-black text-blue-800 uppercase">{visitorCopy.registerResult}</span>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-blue-400 uppercase mb-1">{visitorCopy.company}</label>
                            <input 
                                type="text" 
                                className="w-full p-3 bg-white border border-blue-200 rounded-xl text-sm font-bold" 
                                placeholder={visitorCopy.companyPlaceholder}
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-blue-400 uppercase mb-1">{visitorCopy.note}</label>
                            <textarea 
                                className="w-full p-3 bg-white border border-blue-200 rounded-xl text-sm min-h-[80px]" 
                                placeholder={visitorCopy.notePlaceholder}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <button 
                                onClick={handleSaveNoteOnly}
                                disabled={isSavingNote}
                                className="flex-1 py-3 bg-white border border-blue-600 text-blue-600 rounded-xl font-bold text-sm hover:bg-blue-50 transition-all disabled:opacity-50"
                            >
                                {isSavingNote ? visitorCopy.saving : visitorCopy.saveNote}
                            </button>
                            {visitor.identification_type === 'auto_guess' && (
                                <button 
                                    onClick={handleConfirmName}
                                    disabled={isConfirming || !companyName}
                                    className="flex-1 py-3 bg-indigo-50 border border-indigo-600 text-indigo-600 rounded-xl font-bold text-sm hover:bg-indigo-100"
                                >
                                    {isConfirming ? visitorCopy.processing : visitorCopy.confirmCompany}
                                </button>
                            )}
                            <button 
                                onClick={() => onConvert({ company_name: companyName, notes: notes })}
                                disabled={!companyName}
                                className="flex-1 py-3 bg-green-600 text-white rounded-xl font-black text-sm hover:bg-green-700 shadow-lg disabled:opacity-50 transition-all"
                            >
                                {visitorCopy.convert}
                            </button>
                        </div>
                    </div>

                    {/* 3. デバイス & 流入元情報セクション（新設） */}
                    <div className="grid grid-cols-1 gap-3 mb-6 sm:grid-cols-2">
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <span className="text-[10px] font-black text-gray-400 uppercase block mb-2">{visitorCopy.device}</span>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-gray-800">
                                    {/* User-AgentからOSを簡易判別するロジックがあれば最高 */}
                                    {visitor.user_agent?.includes('Mac') ? '💻 Mac' : 
                                    visitor.user_agent?.includes('Win') ? '💻 Windows' : 
                                    visitor.user_agent?.includes('iPhone') ? '📱 iPhone' : '📱 Mobile/Other'}
                                </span>
                            </div>
                            <div className="text-[9px] text-gray-400 mt-1 font-mono flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="font-bold text-gray-500">{visitor.ip_address}</span>
                                <div className="flex flex-wrap items-center gap-2">
                                    <a 
                                        href={`https://whois.nic.ad.jp/cgi-bin/whois_gw?key=${visitor.ip_address}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline flex items-center gap-0.5"
                                        title={visitorCopy.domesticWhoisTitle}
                                    >
                                        <Icons.Search className="w-2 h-2" /> {visitorCopy.domesticWhois}
                                    </a>
                                    <a 
                                        href={`https://ipinfo.io/${visitor.ip_address}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline flex items-center gap-0.5"
                                        title={visitorCopy.globalWhoisTitle}
                                    >
                                        <Icons.Search className="w-2 h-2" /> {visitorCopy.globalWhois}
                                    </a>
                                    <button 
                                        onClick={() => setShowGuide(true)} 
                                        className="ml-1 bg-gray-100 hover:bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded text-[8px] font-black uppercase transition-colors"
                                    >
                                        {visitorCopy.guideButton}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-orange-50/50 rounded-2xl border border-orange-100/50">
                            <span className="text-[10px] font-black text-orange-400 uppercase block mb-2">{visitorCopy.source}</span>
                            <div className="flex items-center gap-1.5">
                                <div className="text-sm">{getSourceIcon(visitorSourceLabel)}</div>
                                <span className="text-xs font-black text-gray-800">
                                    {visitorSourceLabel}
                                </span>
                            </div>
                            <div className="text-[9px] text-orange-400 mt-1 font-bold uppercase tracking-tighter break-all">
                                {visitorSourceMeta}
                            </div>
                            {visitorSourceDetail && (
                                <div className="text-[9px] text-gray-400 mt-1 break-all" title={visitorSourceDetail}>
                                    {visitorSourceDetail}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 2. 行動履歴（足跡） */}
                    <div>
                        <h4 className="font-black text-xs text-gray-400 uppercase tracking-widest mb-4">{visitorCopy.history}</h4>
                        {isLoading ? (
                            <div className="flex justify-center py-10"><Icons.Refresh className="animate-spin text-gray-300"/></div>
                        ) : (
                            <div className="space-y-4">
                                {timeline.map((item, idx) => (
                                    <div key={idx} className="flex gap-4 items-start bg-white border border-gray-100 p-4 rounded-xl">
                                        <div className="p-2 bg-gray-50 rounded-lg text-gray-400"><Icons.Globe /></div>
                                        <div className="flex-grow min-w-0">
                                            <p className="text-sm font-bold text-gray-800 truncate">{item.summary || copy.untitledPage}</p>
                                            <p className="text-[10px] text-gray-400 mt-1 break-all">{item.detail}</p>
                                            <p className="text-[9px] text-gray-300 font-mono mt-1">{formatLeadDateTime(item.timestamp, isEnglishBeta)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showGuide && <IdentityGuideModal onClose={() => setShowGuide(false)} isEnglishBeta={isEnglishBeta} />}
        </div>
    );
}

// --- 手動登録モーダル ---
function ManualAddLeadModal({ onClose, onSave, isEnglishBeta = false }) {
    const copy = getLeadCopy(isEnglishBeta).manual;
    const [form, setForm] = useState({ company_name: '', name: '', email: '', phone: '', tags: '', memo: '' });
    const [isSaving, setIsSaving] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault(); setIsSaving(true);
        await onSave(form); setIsSaving(false);
    };
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-3 sm:p-4">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-5 sm:p-8 max-h-[92vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="font-black text-xl text-gray-800 uppercase tracking-tight">{copy.title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><Icons.Close /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <input type="text" className="p-2.5 bg-gray-50 border rounded-lg text-sm" placeholder={copy.company} value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} />
                        <input type="text" className="p-2.5 bg-gray-50 border rounded-lg text-sm" placeholder={copy.name} value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                    </div>
                    <input type="email" className="w-full p-2.5 bg-gray-50 border rounded-lg text-sm" placeholder={copy.email} value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                    <input type="tel" className="w-full p-2.5 bg-gray-50 border rounded-lg text-sm" placeholder={copy.phone} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{copy.tags}</label>
                        <input type="text" className="w-full p-2.5 bg-gray-50 border rounded-lg text-sm" placeholder={copy.tagPlaceholder} value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} />
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-6 sm:flex sm:justify-end">
                        <button type="button" onClick={onClose} className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all">{copy.cancel}</button>
                        <button type="submit" disabled={isSaving || (!form.company_name && !form.name && !form.email)} className="px-8 py-2.5 bg-blue-600 text-white font-black rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all disabled:opacity-50">
                            {isSaving ? copy.saving : copy.save}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// --- インポートモーダル ---
function ImportModal({ onClose, onUpload, isEnglishBeta = false }) {
    const copy = getLeadCopy(isEnglishBeta).import;
    const [file, setFile] = useState(null);
    const [tags, setTags] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault(); if (!file) return alert(copy.selectFile);
        setIsUploading(true);
        const formData = new FormData(); formData.append("file", file); formData.append("tags", tags);
        await onUpload(formData); setIsUploading(false);
    };
    const handleDownloadTemplate = async () => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/leads/template`, { headers: getAuthHeader() });
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = "lead_import_template.csv";
            document.body.appendChild(a); a.click(); a.remove();
        } catch (e) { alert(copy.downloadFailed); }
    };
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-3 sm:p-4">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-5 sm:p-8 max-h-[92vh] overflow-y-auto">
                <h3 className="font-black text-xl mb-6 text-gray-800 uppercase tracking-tight">{copy.title}</h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase">{copy.fileLabel}</label>
                        <button type="button" onClick={handleDownloadTemplate} className="text-[10px] text-blue-600 font-bold hover:underline flex items-center gap-1"><Icons.Download /> {copy.template}</button>
                    </div>
                    <input type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="w-full text-xs border-2 border-dashed border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors" required />
                    <input type="text" value={tags} onChange={e => setTags(e.target.value)} className="w-full border p-2.5 bg-gray-50 rounded-lg text-sm" placeholder={copy.tagsPlaceholder} />
                    <div className="grid grid-cols-2 gap-3 pt-4 sm:flex sm:justify-end">
                        <button type="button" onClick={onClose} className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all">{copy.cancel}</button>
                        <button type="submit" disabled={isUploading} className="px-8 py-2.5 bg-blue-600 text-white font-black rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all disabled:opacity-50">
                            {isUploading ? copy.uploading : copy.start}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// --- メインページコンポーネント ---
export default function MarketingLeadsPage({ dealsPath = '/app/deals', accountPath = '/account' }) {
    const { user } = useOutletContext();
    const { clientId } = useOutletContext();
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = getLeadCopy(isEnglishBeta);
    const mainCopy = copy.main;
    const leadCopy = copy.lead;
    const visitorCopy = copy.visitor;
    const aiCopy = copy.ai;

    const [isFreePlan, setIsFreePlan] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    
    // URLパラメータ (?searchTerm=会社名) を取得
    const [searchParams] = useSearchParams();
    const querySearch = searchParams.get('searchTerm') || "";

    const [leads, setLeads] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [anonymousPagination, setAnonymousPagination] = useState(null);
    const [aiPagination, setAiPagination] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // 初期値をURLから取得した値に設定
    const [searchTerm, setSearchTerm] = useState(querySearch);
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);

    // 検索ワードがある場合は「全件表示(all)」から開始、ない場合は「アクティブ(active)」
    const [listCategory, setListCategory] = useState(querySearch ? "all" : "active");

    const [anonymousVisitors, setAnonymousVisitors] = useState([]);
    const [selectedVisitor, setSelectedVisitor] = useState(null);
    const [aiEvents, setAiEvents] = useState([]);
    const [aiStats, setAiStats] = useState([]);
    const [aiEventType, setAiEventType] = useState('all');
    const [accessAnalytics, setAccessAnalytics] = useState(null);
    const [analyticsRange, setAnalyticsRange] = useState('30d');
    const [analyticsBucket, setAnalyticsBucket] = useState('daily');
    const [analyticsStartDate, setAnalyticsStartDate] = useState(() => getDateInputValue(30));
    const [analyticsEndDate, setAnalyticsEndDate] = useState(() => getDateInputValue(0));
    
    const [showImport, setShowImport] = useState(false); 
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);

    const fetchLeads = useCallback(async (page = 1) => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ 
                client_id: clientId, 
                page: String(page), 
                limit: "50", 
                status: statusFilter, 
                searchTerm,
                listCategory: listCategory // ★ 追加：カテゴリを送信
            });
            const res = await fetch(`${API_URL}/api/marketing/leads?${params}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                // --- ★ 追加：フロントエンドでのデータ加工処理 ---
                const processedLeads = data.leads.map(l => {
                    // 1. 生データのパース (memo内の [フォーム送信データ] 以降を抽出)
                    let rawData = null;
                    if (l.memo && l.memo.includes('[フォーム送信データ]')) {
                        try {
                            const jsonPart = l.memo.split('[フォーム送信データ]\n')[1] || l.memo.split('[フォーム送信データ] ')[1];
                            rawData = JSON.parse(jsonPart);
                        } catch (e) {
                            rawData = { [leadCopy.rawError]: leadCopy.rawErrorBody, [leadCopy.rawContent]: l.memo };
                        }
                    } else if (l.memo && l.memo.includes('[自動検知フォーム送信]')) {
                        try {
                            const jsonPart = l.memo.split('[自動検知フォーム送信]\n')[1];
                            rawData = JSON.parse(jsonPart);
                        } catch (e) {
                            rawData = { [leadCopy.rawError]: leadCopy.rawErrorBody, [leadCopy.rawContent]: l.memo };
                        }
                    }

                    // 2. 計測強度の判定
                    let strength = 'low';
                    if (l.meta_lead_id || (l.fbp && l.fbc)) strength = 'high';
                    else if (l.fbp || l.fbc) strength = 'medium';

                    return {
                        ...l,
                        raw_data: rawData, // これでサイドバーが表示できるようになります
                        tracking_strength: strength
                    };
                });

                setLeads(processedLeads); // 加工後のデータをセット
                setPagination(data.pagination);
            }
        } catch (e) { console.error("Fetch Error:", e); }
        finally { setIsLoading(false); }
    }, [clientId, statusFilter, searchTerm, listCategory, leadCopy.rawContent, leadCopy.rawError, leadCopy.rawErrorBody]);

    const fetchAnonymousVisitors = useCallback(async (page = 1) => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ client_id: clientId, page: String(page), limit: "50" });
            const res = await fetch(`${API_URL}/api/marketing/anonymous-visitors?${params}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                setAnonymousVisitors(data.visitors);
                setAnonymousPagination(data.pagination);
            }
        } catch (e) { console.error("Anon Fetch Error:", e); }
        finally { setIsLoading(false); }
    }, [clientId]);

    const fetchAiDiscovery = useCallback(async (page = 1) => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                client_id: clientId,
                page: String(page),
                limit: "50",
                eventType: aiEventType
            });
            const res = await fetch(`${API_URL}/api/marketing/ai-discovery?${params}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                setAiEvents(data.events || []);
                setAiStats(data.stats || []);
                setAiPagination(data.pagination);
            }
        } catch (e) { console.error("AI Discovery Fetch Error:", e); }
        finally { setIsLoading(false); }
    }, [clientId, aiEventType]);

    const fetchAccessAnalytics = useCallback(async () => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                client_id: clientId,
                range: analyticsRange,
                bucket: analyticsBucket
            });
            if (analyticsRange === 'custom') {
                if (!analyticsStartDate || !analyticsEndDate) {
                    setAccessAnalytics(null);
                    return;
                }
                params.set('start_date', analyticsStartDate);
                params.set('end_date', analyticsEndDate);
            }
            const res = await fetch(`${API_URL}/api/marketing/access-analytics?${params}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                setAccessAnalytics(data);
            }
        } catch (e) { console.error("Access Analytics Fetch Error:", e); }
        finally { setIsLoading(false); }
    }, [clientId, analyticsRange, analyticsBucket, analyticsStartDate, analyticsEndDate]);

    useEffect(() => {
        if (!clientId) return;
        fetch(`${API_URL}/api/usage-status?clientId=${clientId}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => { 
                if (data.success) {
                    // トライアル(trial)以外でfreeプランなら制限
                    setIsFreePlan(data.plan === 'free');
                }
            });
    }, [clientId]);

    useEffect(() => {
        // --- ★ 追加: 無料プラン時のガード ---
        const isFreePlanLimitedList = listCategory !== 'anonymous' && listCategory !== 'ai' && listCategory !== 'analytics';
        if (isFreePlan && isFreePlanLimitedList) {
            // 2ページ目以降、または検索ワードが入っている場合は遮断
            if (currentPage > 1 || searchTerm.length > 0) {
                setShowUpgradeModal(true);
                // ステートを戻す
                if (currentPage > 1) setCurrentPage(1);
                if (searchTerm.length > 0) setSearchTerm("");
                return;
            }
        }

        const timer = setTimeout(() => { 
            if (listCategory === 'anonymous') {
                fetchAnonymousVisitors(currentPage);
            } else if (listCategory === 'ai') {
                fetchAiDiscovery(currentPage);
            } else if (listCategory === 'analytics') {
                fetchAccessAnalytics();
            } else {
                fetchLeads(currentPage);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchLeads, fetchAnonymousVisitors, fetchAiDiscovery, fetchAccessAnalytics, currentPage, listCategory, isFreePlan, searchTerm.length]); // isFreePlanを監視対象に追加

    const handleUpdateLead = async (id, updateData) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/leads/${id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId, ...updateData })
            });
            const data = await res.json();
            if (data.success) {
                fetchLeads(currentPage);
                setLeads(prev => prev.map(l => l.id === id ? { ...l, ...updateData } : l));
                return true;
            }
            return false;
        } catch (e) { alert(copy.updateError); return false; }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await fetch(`${API_URL}/api/marketing/leads/${id}/status`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId, status: newStatus })
            });
            fetchLeads(currentPage);
        } catch (e) { alert(copy.updateFailed); }
    };

    const handleConvert = async (id) => {
        if (!window.confirm(copy.convertConfirm)) return false;
        try {
            const res = await fetch(`${API_URL}/api/marketing/leads/${id}/convert`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId })
            });
            const data = await res.json();
            if (data.success) {
                if (window.confirm(copy.convertSuccessConfirm)) {
                    const lead = leads.find(l => l.id === id);
                    window.location.href = `${dealsPath}?searchTerm=${encodeURIComponent(lead ? (lead.company_name || lead.name) : "")}`;
                } else {
                    fetchLeads(currentPage);
                }
                return true;
            } else {
                alert(copy.errorPrefix + data.error);
                return false;
            }
        } catch (e) { alert(e.message); return false; }
    };

    const handleImport = async (formData) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/leads/import`, { method: "POST", headers: getAuthHeader(), body: formData });
            const data = await res.json();
            if (data.success) { alert(isEnglishBeta ? copy.import.success : data.message); setShowImport(false); fetchLeads(1); }
            else { alert(copy.importFailedPrefix + data.error); }
        } catch (e) { alert(e.message); }
    };
    
    const handleManualAdd = async (formData) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/leads`, { method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeader() }, body: JSON.stringify({ client_id: clientId, ...formData }) });
            if ((await res.json()).success) { setShowAddModal(false); fetchLeads(1); }
            else { alert(copy.manualAddFailed); }
        } catch (e) { console.error(e); }
    };

    const handleCalculateScore = async () => {
        if (!window.confirm(copy.scoreConfirm)) return;
        setIsCalculating(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/leads/score/calculate`, { method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeader() }, body: JSON.stringify({ client_id: clientId }) });
            if ((await res.json()).success) fetchLeads(currentPage);
        } finally { setIsCalculating(false); }
    };

    const aiReferralCount = aiStats
        .filter(item => item.event_type === 'ai_referral')
        .reduce((sum, item) => sum + Number(item.count || 0), 0);
    const aiCrawlerCount = aiStats
        .filter(item => item.event_type === 'ai_crawler')
        .reduce((sum, item) => sum + Number(item.count || 0), 0);
    const latestAiDetectedAt = aiStats
        .map(item => item.last_detected_at)
        .filter(Boolean)
        .sort()
        .pop();
    const activePagination = listCategory === 'anonymous'
        ? anonymousPagination
        : listCategory === 'ai'
            ? aiPagination
            : listCategory === 'analytics'
                ? null
            : pagination;
    const activeTotalPages = Number(activePagination?.totalPages || 0);
    const showFreePaginationNotice = isFreePlan && listCategory !== 'anonymous' && listCategory !== 'ai' && listCategory !== 'analytics' && activeTotalPages > 1;
    const showPaginationFooter = activeTotalPages > 1 || showFreePaginationNotice;

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <div className="max-w-[1400px] mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 uppercase tracking-tight">{mainCopy.title}</h1>
                        <p className="text-sm text-gray-500 font-medium">{mainCopy.subtitle}</p>
                    </div>
                    <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 md:w-auto md:flex md:flex-wrap">
                        <button onClick={handleCalculateScore} disabled={isCalculating} className="flex items-center justify-center gap-2 bg-white text-gray-600 border px-4 py-2.5 rounded-xl hover:bg-gray-50 font-bold shadow-sm transition-all disabled:opacity-50">
                            <Icons.Refresh className={isCalculating ? "animate-spin" : ""} />
                            {isCalculating ? mainCopy.calculating : mainCopy.recalc}
                        </button>
                        <button onClick={() => setShowAddModal(true)} className="flex items-center justify-center gap-2 bg-white text-gray-600 border px-4 py-2.5 rounded-xl hover:bg-gray-50 font-bold shadow-sm transition-all">
                            <Icons.Plus /> {mainCopy.add}
                        </button>
                        <button onClick={() => setShowImport(true)} className="flex items-center justify-center gap-2 bg-blue-600 text-white font-black py-2.5 px-6 rounded-xl hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all">
                            <Icons.Upload /> {mainCopy.importCsv}
                        </button>
                    </div>
                </div>

                <div className="flex overflow-x-auto border-b border-gray-200 mb-6 sm:mb-8 bg-white px-2 sm:px-6 rounded-t-2xl shadow-sm">
                    {copy.tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => { setListCategory(tab.id); setCurrentPage(1); }}
                            className={`relative shrink-0 py-4 px-4 sm:px-8 transition-all duration-300 ${
                                listCategory === tab.id 
                                ? 'text-blue-600' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                        >
                            <div className="flex flex-col items-center">
                                <span className={`text-xs font-black uppercase tracking-widest mb-1 ${listCategory === tab.id ? 'opacity-100' : 'opacity-40'}`}>
                                    {tab.sub}
                                </span>
                                <span className="text-sm font-black tracking-tight">{tab.label}</span>
                            </div>
                            {listCategory === tab.id && (
                                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t-full animate-in fade-in slide-in-from-bottom-1" />
                            )}
                        </button>
                    ))}
                </div>
                
                {listCategory === 'analytics' ? (
                    <div className="bg-white p-3 sm:p-4 rounded-2xl shadow-sm mb-6 border border-blue-100">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{copy.analytics.rangeLabel}</label>
                                <select className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm font-bold text-gray-600 focus:ring-2 focus:ring-blue-500 sm:w-auto sm:min-w-[180px]" value={analyticsRange} onChange={e => setAnalyticsRange(e.target.value)}>
                                    {copy.analytics.ranges.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            {analyticsRange === 'custom' && (
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{copy.analytics.startDate}</label>
                                        <input
                                            type="date"
                                            className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm font-bold text-gray-600 focus:ring-2 focus:ring-blue-500 sm:w-auto"
                                            value={analyticsStartDate}
                                            max={analyticsEndDate}
                                            onChange={e => setAnalyticsStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{copy.analytics.endDate}</label>
                                        <input
                                            type="date"
                                            className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm font-bold text-gray-600 focus:ring-2 focus:ring-blue-500 sm:w-auto"
                                            value={analyticsEndDate}
                                            min={analyticsStartDate}
                                            onChange={e => setAnalyticsEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{copy.analytics.bucketLabel}</label>
                                <select className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm font-bold text-gray-600 focus:ring-2 focus:ring-blue-500 sm:w-auto sm:min-w-[140px]" value={analyticsBucket} onChange={e => setAnalyticsBucket(e.target.value)}>
                                    {copy.analytics.buckets.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                ) : listCategory === 'ai' ? (
                    <div className="bg-white p-3 sm:p-4 rounded-2xl shadow-sm mb-6 border border-violet-100">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div className="rounded-2xl bg-violet-50 p-4 border border-violet-100">
                                <p className="text-[10px] font-black uppercase tracking-widest text-violet-400">{aiCopy.aiReferral}</p>
                                <p className="mt-1 text-2xl font-black text-violet-700">{aiReferralCount}</p>
                            </div>
                            <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{aiCopy.aiCrawler}</p>
                                <p className="mt-1 text-2xl font-black text-slate-700">{aiCrawlerCount}</p>
                            </div>
                            <div className="rounded-2xl bg-white p-4 border border-gray-100">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{aiCopy.latest}</p>
                                <p className="mt-1 text-sm font-black text-gray-700">{latestAiDetectedAt ? formatLeadDateTime(latestAiDetectedAt, isEnglishBeta) : '-'}</p>
                            </div>
                        </div>
                        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">{aiCopy.filterLabel}</label>
                            <select className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm font-bold text-gray-600 focus:ring-2 focus:ring-violet-500 sm:w-auto sm:min-w-[220px]" value={aiEventType} onChange={e => { setAiEventType(e.target.value); setCurrentPage(1); setAiPagination(null); }}>
                                <option value="all">{aiCopy.all}</option>
                                <option value="ai_referral">{aiCopy.aiReferral}</option>
                                <option value="ai_crawler">{aiCopy.aiCrawler}</option>
                            </select>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white p-3 sm:p-4 rounded-2xl shadow-sm mb-6 flex flex-col md:flex-row gap-3 sm:gap-4 border border-gray-100">
                        <div className="relative flex-grow">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400"><Icons.Search /></div>
                            <input type="text" className="w-full pl-11 p-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-blue-500 transition-all" placeholder={mainCopy.searchPlaceholder} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        </div>
                        <select className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm font-bold text-gray-600 focus:ring-2 focus:ring-blue-500 md:w-auto md:min-w-[200px]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            {copy.filterStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                {listCategory === 'analytics' ? (
                    <AccessAnalyticsPanel analytics={accessAnalytics} isLoading={isLoading} isEnglishBeta={isEnglishBeta} />
                ) : (
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
                    <div className="divide-y divide-gray-100 md:hidden">
                        {isLoading ? (
                            <div className="p-8 text-center">
                                <Icons.Refresh className="animate-spin inline-block text-blue-500 w-8 h-8"/>
                            </div>
                        ) : listCategory === 'anonymous' ? (
                            anonymousVisitors.length === 0 ? (
                                <div className="p-8 text-center text-gray-400 font-bold">{mainCopy.noVisitors}</div>
                            ) : (
                                anonymousVisitors.map(visitor => (
                                    <div key={`${visitor.visitor_id}-mobile`} className="p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                {visitor.company_name ? (
                                                    <>
                                                        <div className="flex flex-wrap items-center gap-1.5">
                                                            {visitor.identification_type === 'auto_guess' ? (
                                                                <span className="bg-amber-100 text-amber-700 text-[9px] font-black px-1.5 py-0.5 rounded uppercase border border-amber-200">{visitorCopy.estimated}</span>
                                                            ) : (
                                                                <span className="bg-indigo-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded uppercase flex items-center gap-1">
                                                                    <Icons.Shield /> {visitorCopy.identified}
                                                                </span>
                                                            )}
                                                            <span className="break-words font-black text-gray-900">{visitor.company_name}</span>
                                                        </div>
                                                        <span className="mt-1 block text-[10px] text-gray-400 font-mono">UUID: {visitor.visitor_id.substring(0, 12)}...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="font-bold text-gray-400 italic">{visitorCopy.unidentified}</span>
                                                        <span className="mt-1 block text-[10px] text-gray-300 font-mono">UUID: {visitor.visitor_id.substring(0, 12)}...</span>
                                                    </>
                                                )}
                                            </div>
                                            <span className="shrink-0 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg font-black text-xs">{visitor.total_pvs} PV</span>
                                        </div>
                                        <div className="mt-3 space-y-1 text-xs text-gray-600">
                                            <p><span className="font-semibold text-gray-500">{visitorCopy.lastVisit}:</span> {formatLeadDateTime(visitor.last_visited_at, isEnglishBeta)}</p>
                                            <p className="break-words"><span className="font-semibold text-gray-500">{visitorCopy.lastPage}:</span> {visitor.last_view_title || copy.untitledPage}</p>
                                            <p className="break-all text-gray-400">{visitor.last_view_url}</p>
                                            <p className="flex flex-wrap items-center gap-1.5">
                                                <span className="font-semibold text-gray-500">{visitorCopy.source}:</span>
                                                {getSourceIcon(getVisitorSourceLabel(visitor, isEnglishBeta))}
                                                <span className="font-bold text-gray-700">{getVisitorSourceLabel(visitor, isEnglishBeta)}</span>
                                                <span className="text-gray-400">{getVisitorSourceMeta(visitor)}</span>
                                            </p>
                                            <p><span className="font-semibold text-gray-500">IP:</span> {visitor.ip_address}</p>
                                        </div>
                                        <button onClick={() => setSelectedVisitor(visitor)} className="mt-4 w-full rounded-xl bg-blue-50 px-3 py-2 text-xs font-black text-blue-600">
                                            {visitorCopy.analyze}
                                        </button>
                                    </div>
                                ))
                            )
                        ) : listCategory === 'ai' ? (
                            aiEvents.length === 0 ? (
                                <div className="p-8 text-center text-gray-400 font-bold">{mainCopy.noAiEvents}</div>
                            ) : (
                                aiEvents.map(event => (
                                    <div key={`${event.id}-mobile`} className="p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase ${event.event_type === 'ai_crawler' ? 'bg-slate-100 text-slate-600' : 'bg-violet-100 text-violet-700'}`}>
                                                        {event.event_type === 'ai_crawler' ? aiCopy.aiCrawler : aiCopy.aiReferral}
                                                    </span>
                                                    <span className="font-black text-gray-900">{event.source_name}</span>
                                                </div>
                                                <p className="mt-1 text-[10px] text-gray-400 font-mono">{formatLeadDateTime(event.detected_at, isEnglishBeta)}</p>
                                            </div>
                                            <span className="shrink-0 rounded-lg bg-gray-100 px-2.5 py-1 text-[10px] font-black text-gray-600">
                                                {event.lead_id ? aiCopy.linkedLead : aiCopy.anonymous}
                                            </span>
                                        </div>
                                        <div className="mt-3 space-y-1 text-xs text-gray-600">
                                            <p className="break-words"><span className="font-semibold text-gray-500">{aiCopy.page}:</span> {event.page_title || copy.untitledPage}</p>
                                            <p className="break-all text-gray-400">{event.url || event.landing_url}</p>
                                            {event.referrer && <p className="break-all text-gray-400"><span className="font-semibold text-gray-500">{aiCopy.referrer}:</span> {event.referrer}</p>}
                                            <p><span className="font-semibold text-gray-500">IP:</span> {event.ip_address || '-'}</p>
                                            {event.lead_id && (
                                                <p className="break-words"><span className="font-semibold text-gray-500">{aiCopy.lead}:</span> {event.company_name || event.name || event.email || `ID ${event.lead_id}`}</p>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )
                        ) : (
                            leads.length === 0 ? (
                                <div className="p-8 text-center text-gray-400 font-bold">{mainCopy.noLeads}</div>
                            ) : (
                                leads.map(lead => (
                                    <div key={`${lead.id}-mobile`} className={`p-4 ${lead.status === 'new' ? 'bg-blue-50/30' : 'bg-white'}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <button onClick={() => setSelectedLead(lead)} className="min-w-0 text-left">
                                                <p className="break-words font-black text-gray-900">{lead.company_name || lead.name || copy.unnamedShort}</p>
                                                <p className="mt-1 break-all text-xs text-gray-500">{lead.name}{lead.email ? ` / ${lead.email}` : ''}</p>
                                            </button>
                                            <div className="flex shrink-0 flex-col items-end gap-1">
                                                {getSourceIcon(lead.source_label)}
                                                <span className="text-[9px] font-black text-gray-500 uppercase">{lead.source_label}</span>
                                            </div>
                                        </div>
                                        {lead.tags && (
                                            <div className="mt-3 flex flex-wrap gap-1">
                                                {lead.tags.split(',').map((t, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] rounded-md font-bold border border-gray-200">
                                                        {t.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                            <p><span className="font-semibold text-gray-500">{leadCopy.createdAt}</span><br />{formatLeadDateTime(lead.created_at, isEnglishBeta)}</p>
                                            <p><span className="font-semibold text-gray-500">{leadCopy.status}</span><br /><span className={`font-black uppercase ${lead.status === 'new' ? 'text-blue-600' : lead.status === 'converted' ? 'text-green-600' : 'text-gray-400'}`}>{getStatusLabel(lead.status, isEnglishBeta)}</span></p>
                                            <p><span className="font-semibold text-gray-500">{leadCopy.score}</span><br /><span className="font-black text-indigo-600">{lead.score || 0}</span> Points</p>
                                            <p><span className="font-semibold text-gray-500">{leadCopy.clicks}</span><br />{lead.click_count > 0 ? `${lead.click_count} Clicks` : '-'}</p>
                                        </div>
                                        <div className="mt-4 grid grid-cols-2 gap-2">
                                            <button onClick={() => setSelectedLead(lead)} className="flex items-center justify-center gap-1 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-600">
                                                <Icons.Eye /> {leadCopy.detail}
                                            </button>
                                            {lead.status !== 'converted' && (
                                                <button onClick={() => handleConvert(lead.id)} className="flex items-center justify-center gap-1 rounded-xl bg-green-600 px-3 py-2 text-xs font-black text-white">
                                                    <Icons.ArrowRight /> {leadCopy.convert}
                                                </button>
                                            )}
                                            {lead.status !== 'archived' && (
                                                <button
                                                    onClick={() => {
                                                        if(window.confirm(copy.archiveConfirm)) {
                                                            handleStatusChange(lead.id, 'archived');
                                                        }
                                                    }}
                                                    className="col-span-2 flex items-center justify-center gap-1 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-bold text-red-600"
                                                >
                                                    <Icons.Archive className="w-3.5 h-3.5" /> {leadCopy.archive}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )
                        )}
                    </div>
                    <div className="hidden overflow-x-auto md:block">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b">
                                {listCategory === 'anonymous' ? (
                                    /* --- 匿名ログ専用ヘッダー --- */
                                    <tr>
                                        <th className="p-5">{visitorCopy.lastVisit}</th>
                                        <th className="p-5">{visitorCopy.visitorCol}</th>
                                        <th className="p-5 text-center">{visitorCopy.totalPv}</th>
                                        <th className="p-5">{visitorCopy.lastPage}</th>
                                        <th className="p-5">{visitorCopy.ipAddress}</th>
                                        <th className="p-5 text-right">{leadCopy.actions}</th>
                                    </tr>
                                ) : listCategory === 'ai' ? (
                                    <tr>
                                        <th className="p-5">{aiCopy.detectedAt}</th>
                                        <th className="p-5">{aiCopy.type}</th>
                                        <th className="p-5">{aiCopy.source}</th>
                                        <th className="p-5">{aiCopy.page}</th>
                                        <th className="p-5">{aiCopy.referrer}</th>
                                        <th className="p-5">{aiCopy.lead}</th>
                                        <th className="p-5">{visitorCopy.ipAddress}</th>
                                    </tr>
                                ) : (
                                    /* --- 通常リード用ヘッダー --- */
                                    <tr>
                                        <th className="p-5">{leadCopy.createdAt}</th>
                                        <th className="p-5 text-center">{leadCopy.source}</th>
                                        <th className="p-5">{leadCopy.leadName}</th>
                                        <th className="p-5 text-center">{leadCopy.trackingStrength}</th>
                                        <th className="p-5 text-center">{leadCopy.score}</th>
                                        <th className="p-5 text-center">{leadCopy.clicks}</th>
                                        <th className="p-5">{leadCopy.status}</th>
                                        <th className="p-5 text-right">{leadCopy.actions}</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={listCategory === 'anonymous' ? 6 : listCategory === 'ai' ? 7 : 8} className="p-20 text-center">
                                            <Icons.Refresh className="animate-spin inline-block text-blue-500 w-8 h-8"/>
                                        </td>
                                    </tr>
                                ) : listCategory === 'anonymous' ? (
                                    /* --- 匿名ログのデータレンダリング --- */
                                    anonymousVisitors.length === 0 ? (
                                        <tr><td colSpan="6" className="p-20 text-center text-gray-400 font-bold">{mainCopy.noVisitors}</td></tr>
                                    ) : (
                                        anonymousVisitors.map(visitor => (
                                            <tr key={visitor.visitor_id} className="group hover:bg-gray-50/80 transition-all">
                                                <td className="p-5 whitespace-nowrap">
                                                    <div className="text-gray-900 font-medium">{formatLeadDate(visitor.last_visited_at, isEnglishBeta)}</div>
                                                    <div className="text-[10px] text-gray-400 font-mono">{formatLeadTime(visitor.last_visited_at, isEnglishBeta)}</div>
                                                </td>
                                                <td className="p-5">
                                                    {visitor.company_name ? (
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center gap-1.5">
                                                                {/* ステータスによってバッジを出し分け */}
                                                                {visitor.identification_type === 'auto_guess' ? (
                                                                    <span className="bg-amber-100 text-amber-700 text-[9px] font-black px-1.5 py-0.5 rounded uppercase flex items-center gap-1 border border-amber-200">
                                                                        ⚠️ {visitorCopy.estimated}
                                                                    </span>
                                                                ) : (
                                                                    <span className="bg-indigo-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded uppercase flex items-center gap-1">
                                                                        <Icons.Shield /> {visitorCopy.identified}
                                                                    </span>
                                                                )}
                                                                <span className="font-black text-gray-900">{visitor.company_name}</span>
                                                            </div>
                                                            <span className="text-[10px] text-gray-400 font-mono mt-1">UUID: {visitor.visitor_id.substring(0, 12)}...</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-gray-400 italic">{visitorCopy.unidentified}</span>
                                                            <span className="text-[10px] text-gray-300 font-mono">UUID: {visitor.visitor_id.substring(0, 12)}...</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-5 text-center">
                                                    <div className="inline-flex flex-col items-center">
                                                        <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg font-black text-xs">{visitor.total_pvs} PV</span>
                                                    </div>
                                                </td>
                                                <td className="p-5">
                                                    <div className="text-xs text-blue-600 font-bold truncate max-w-[200px]" title={visitor.last_view_title}>
                                                        {visitor.last_view_title || copy.untitledPage}
                                                    </div>
                                                    <div className="text-[9px] text-gray-400 truncate max-w-[200px]" title={visitor.last_view_url}>
                                                        {visitor.last_view_url}
                                                    </div>
                                                    <div
                                                        className="mt-1 flex items-center gap-1 text-[9px] text-gray-500 truncate max-w-[200px]"
                                                        title={getVisitorSourceDetail(visitor) || getVisitorSourceLabel(visitor, isEnglishBeta)}
                                                    >
                                                        {getSourceIcon(getVisitorSourceLabel(visitor, isEnglishBeta))}
                                                        <span className="font-bold truncate">{getVisitorSourceLabel(visitor, isEnglishBeta)}</span>
                                                        <span className="text-gray-400 truncate">{getVisitorSourceMeta(visitor)}</span>
                                                    </div>
                                                </td>
                                                <td className="p-5">
                                                    <span className="text-[10px] font-mono text-gray-500">{visitor.ip_address}</span>
                                                </td>
                                                <td className="p-5 text-right">
                                                    <button 
                                                        onClick={() => setSelectedVisitor(visitor)}
                                                        className="text-[10px] font-black text-blue-600 hover:underline"
                                                    >
                                                        {visitorCopy.analyze}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )
                                ) : listCategory === 'ai' ? (
                                    aiEvents.length === 0 ? (
                                        <tr><td colSpan="7" className="p-20 text-center text-gray-400 font-bold">{mainCopy.noAiEvents}</td></tr>
                                    ) : (
                                        aiEvents.map(event => (
                                            <tr key={event.id} className="group hover:bg-violet-50/30 transition-all">
                                                <td className="p-5 whitespace-nowrap">
                                                    <div className="text-gray-900 font-medium">{formatLeadDate(event.detected_at, isEnglishBeta)}</div>
                                                    <div className="text-[10px] text-gray-400 font-mono">{formatLeadTime(event.detected_at, isEnglishBeta, { hour: '2-digit', minute: '2-digit' })}</div>
                                                </td>
                                                <td className="p-5">
                                                    <span className={`rounded-full px-2 py-1 text-[9px] font-black uppercase ${event.event_type === 'ai_crawler' ? 'bg-slate-100 text-slate-600' : 'bg-violet-100 text-violet-700'}`}>
                                                        {event.event_type === 'ai_crawler' ? aiCopy.aiCrawler : aiCopy.aiReferral}
                                                    </span>
                                                </td>
                                                <td className="p-5">
                                                    <div className="font-black text-gray-900">{event.source_name}</div>
                                                    <div className="text-[10px] text-gray-400 font-mono">{event.source_domain || '-'}</div>
                                                </td>
                                                <td className="p-5">
                                                    <div className="max-w-[240px] truncate text-xs font-bold text-blue-600" title={event.page_title || event.url}>
                                                        {event.page_title || copy.untitledPage}
                                                    </div>
                                                    <div className="max-w-[240px] truncate text-[9px] text-gray-400" title={event.url || event.landing_url}>
                                                        {event.url || event.landing_url}
                                                    </div>
                                                </td>
                                                <td className="p-5">
                                                    <div className="max-w-[220px] truncate text-[10px] text-gray-500" title={event.referrer || ''}>
                                                        {event.referrer || '-'}
                                                    </div>
                                                </td>
                                                <td className="p-5">
                                                    {event.lead_id ? (
                                                        <div>
                                                            <div className="font-black text-green-600 text-xs">{aiCopy.linkedLead}</div>
                                                            <div className="max-w-[180px] truncate text-[10px] text-gray-500" title={event.company_name || event.name || event.email || ''}>
                                                                {event.company_name || event.name || event.email || `ID ${event.lead_id}`}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-[10px] font-black text-gray-400">{aiCopy.anonymous}</span>
                                                    )}
                                                </td>
                                                <td className="p-5">
                                                    <span className="text-[10px] font-mono text-gray-500">{event.ip_address || '-'}</span>
                                                </td>
                                            </tr>
                                        ))
                                    )
                                ) : (
                                    /* --- 通常リードのデータレンダリング (既存) --- */
                                    leads.length === 0 ? (
                                        <tr><td colSpan="8" className="p-20 text-center text-gray-400 font-bold">{mainCopy.noLeads}</td></tr>
                                    ) : (
                                        leads.map(lead => (
                                            <tr key={lead.id} className={`group transition-all ${lead.status === 'new' ? 'bg-blue-50/30' : 'hover:bg-gray-50/80'}`}>
                                                <td className="p-5 whitespace-nowrap">
                                                    <div className="text-gray-900 font-medium">{formatLeadDate(lead.created_at, isEnglishBeta)}</div>
                                                    <div className="text-[10px] text-gray-400 font-mono">{formatLeadTime(lead.created_at, isEnglishBeta, { hour: '2-digit', minute: '2-digit' })}</div>
                                                </td>
                                                <td className="p-5">
                                                    <div className="flex flex-col items-center gap-1">
                                                        {getSourceIcon(lead.source_label)}
                                                        <span className="text-[9px] font-black text-gray-500 uppercase">{lead.source_label}</span>
                                                    </div>
                                                </td>
                                                <td className="p-5">
                                                    <button onClick={() => setSelectedLead(lead)} className="font-black text-gray-900 hover:text-blue-600 text-left block w-full mb-0.5 transition-colors">
                                                        {lead.company_name || lead.name || copy.unnamedShort}
                                                    </button>
                                                    <div className="text-xs text-gray-500 font-medium flex items-center gap-2 mb-1.5">
                                                        {lead.name}
                                                        {lead.email && <span className="text-[10px] text-gray-300">|</span>}
                                                        <span className="text-[10px]">{lead.email}</span>
                                                    </div>
                                                    
                                                    {/* ★ 新設：タグのバッジ表示 */}
                                                    {lead.tags && (
                                                        <div className="flex flex-wrap gap-1">
                                                            {lead.tags.split(',').map((t, i) => (
                                                                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[9px] rounded-md font-bold border border-gray-200">
                                                                    {t.trim()}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-5">
                                                    <div className="flex justify-center">
                                                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter shadow-sm border ${
                                                            lead.tracking_strength === 'high' ? 'bg-green-500 text-white border-green-600' :
                                                            lead.tracking_strength === 'medium' ? 'bg-yellow-400 text-white border-yellow-500' : 'bg-gray-100 text-gray-400 border-gray-200'
                                                        }`}>
                                                            <Icons.Shield /> {lead.tracking_strength === 'high' ? 'CAPI OK' : lead.tracking_strength}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-5 text-center">
                                                    <div className="inline-flex flex-col items-center">
                                                        <span className="text-lg font-black text-indigo-600 leading-none">{lead.score || 0}</span>
                                                        <span className="text-[8px] font-bold text-indigo-300 uppercase">Points</span>
                                                    </div>
                                                </td>
                                                <td className="p-5 text-center">
                                                    {lead.click_count > 0 ? (
                                                        <div className="flex flex-col items-center">
                                                            <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-600 rounded-lg text-[10px] font-black"><Icons.Zap className="w-3 h-3"/> {lead.click_count}</div>
                                                            <div className="text-[8px] text-gray-400 mt-1 uppercase font-mono">Clicks</div>
                                                        </div>
                                                    ) : <span className="text-gray-200">-</span>}
                                                </td>
                                                <td className="p-5">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${lead.status === 'new' ? 'bg-blue-500 animate-pulse' : lead.status === 'converted' ? 'bg-green-500' : 'bg-gray-300'}`} />
                                                        <span className={`text-[10px] font-black uppercase ${lead.status === 'new' ? 'text-blue-600' : lead.status === 'converted' ? 'text-green-600' : 'text-gray-400'}`}>{getStatusLabel(lead.status, isEnglishBeta)}</span>
                                                    </div>
                                                </td>
                                               <td className="p-5 text-right">
                                                    <div className="flex justify-end items-center gap-2">
                                                        <button onClick={() => setSelectedLead(lead)} className="flex items-center gap-1 px-2 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all border border-transparent hover:border-blue-100" title={leadCopy.detailTitle}>
                                                            <Icons.Eye /><span className="text-[10px] font-bold">{leadCopy.detail}</span>
                                                        </button>
                                                        {lead.status !== 'converted' && (
                                                            <button onClick={() => handleConvert(lead.id)} className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 shadow-sm shadow-green-100 transition-all" title={leadCopy.convertTitle}>
                                                                <Icons.ArrowRight /><span className="text-[10px] font-black uppercase">{leadCopy.convert}</span>
                                                            </button>
                                                        )}
                                                        
                                                        {/* ★ アーカイブボタンを文字入り＆確認アラート付きに改善 */}
                                                        {lead.status !== 'archived' && (
                                                            <button 
                                                                onClick={() => {
                                                                    if(window.confirm(copy.archiveConfirm)) {
                                                                        handleStatusChange(lead.id, 'archived');
                                                                    }
                                                                }} 
                                                                className="flex items-center gap-1 px-2 py-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                                            >
                                                                <Icons.Archive className="w-3.5 h-3.5" />
                                                                <span className="text-[10px] font-bold">{leadCopy.archive}</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                    {activePagination && showPaginationFooter && (
                        <div className="p-6 bg-gray-50/30 border-t border-gray-100">
                            {/* ★ 修正: totalPagesを無料なら1に固定 */}
                            <Pagination 
                                currentPage={currentPage} 
                                totalPages={isFreePlan && listCategory !== 'anonymous' && listCategory !== 'ai' && listCategory !== 'analytics' ? 1 : activeTotalPages} 
                                onPageChange={setCurrentPage}
                                isEnglishBeta={isEnglishBeta}
                            />
                            
                            {/* ★ 追加: 無料プラン向けのメッセージ */}
                            {showFreePaginationNotice && (
                                <div className="text-center mt-4">
                                    <p className="text-xs text-amber-600 font-bold bg-amber-50 inline-block px-4 py-2 rounded-full border border-amber-100">
                                        {mainCopy.freeNotice}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                )}
            </div>

            {showImport && <ImportModal onClose={() => setShowImport(false)} onUpload={handleImport} isEnglishBeta={isEnglishBeta} />}
            {showAddModal && <ManualAddLeadModal onClose={() => setShowAddModal(false)} onSave={handleManualAdd} isEnglishBeta={isEnglishBeta} />}
            {selectedLead && (
                <LeadDetailSidebar 
                    lead={selectedLead} 
                    clientId={clientId} 
                    onClose={() => setSelectedLead(null)} 
                    onUpdate={handleUpdateLead} 
                    onConvert={handleConvert}
                    isEnglishBeta={isEnglishBeta}
                    dealsPath={dealsPath}
                />
            )}
            {selectedVisitor && (
                <VisitorDetailSidebar 
                    visitor={selectedVisitor}
                    clientId={clientId}
                    onClose={() => setSelectedVisitor(null)}
                    onConfirmSuccess={() => fetchAnonymousVisitors(currentPage)}
                    onConvert={async (data) => {
                        const res = await fetch(`${API_URL}/api/marketing/visitors/convert`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", ...getAuthHeader() },
                            body: JSON.stringify({ ...data, visitor_id: selectedVisitor.visitor_id, client_id: clientId })
                        });
                        const result = await res.json();
                        if (result.success) {
                            alert(copy.visitorConvertSuccess);
                            setSelectedVisitor(null);
                            // 必要ならリストを再読込
                            if (typeof fetchAnonymousVisitors === 'function') fetchAnonymousVisitors(currentPage);
                        }
                    }}
                    isEnglishBeta={isEnglishBeta}
                />
            )}
            {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} user={user} isEnglishBeta={isEnglishBeta} accountPath={accountPath} />}
        </div>
    );
}
